import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, FileText, KeyRound, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../../utils/Auth/AuthProvider';
import { Badge, Button, Callout, ConfirmDialog, FormField, PagedFooter, PageHeader, PillTabs, RadioCards, Segmented, Spinner, Track } from '../../school_panel/components/ui';
import usePagination from '../../school_panel/lib/usePagination';
import RagSetupProgress from './rag_setup/RagSetupProgress';
import RagStepHeader from './rag_setup/RagStepHeader';
import RagStepFooter from './rag_setup/RagStepFooter';
import RagDevTools from './rag_setup/RagDevTools';
import { RAG_CHECK, RAG_FILE_INPUT, RAG_INPUT, RAG_SECTION_LABEL } from './rag_setup/steps';
import { aiBatchGenerate, aiEmbedContent } from '../../utils/remote';
import { PDFDocument } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';
// Use Vite to load worker as classic script URL to avoid HMR overlays in worker
// @ts-ignore - url import for worker
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

import { request } from '../../utils/remote';
import { LINK_BTN } from '../../school_panel/components/ui/linkButton';
import { plural } from '../../school_panel/lib/plural';

// Configure pdf.js to use classic worker by URL
(pdfjsLib as any).GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

async function extractPdfTextRange(file: File, startPage: number, endPage: number): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const loadingTask = (pdfjsLib as any).getDocument({ data: arrayBuffer });
  const pdf = await loadingTask.promise;
  const total = pdf.numPages as number;
  const s = Math.max(1, Math.min(startPage, total));
  const e = Math.max(s, Math.min(endPage, total));
  let chunks: string[] = [];
  for (let p = s; p <= e; p++) {
    const page = await pdf.getPage(p);
    const content = await page.getTextContent();
    const text = (content.items || []).map((it: any) => it.str).join(' ').replace(/\0/g, '');
    chunks.push(`\n\n=== Page ${p} ===\n` + text);
  }
  try { await pdf.destroy?.(); } catch {}
  return chunks.join('\n');
}

// Helpers for chunking by page with a single loaded PDF
async function loadPdfDocument(file: File): Promise<any> {
  const arrayBuffer = await file.arrayBuffer();
  const loadingTask = (pdfjsLib as any).getDocument({ data: arrayBuffer });
  const pdf = await loadingTask.promise;
  return pdf;
}

async function extractPageText(pdf: any, pageNumber: number): Promise<string> {
  const page = await pdf.getPage(pageNumber);
  const content = await page.getTextContent();
  const text = (content.items || []).map((it: any) => it.str).join(' ').replace(/\0/g, '');
  return text;
}

// Get the PDF outline (bookmark tree) and resolve page numbers when possible
async function getPdfOutlineTree(pdf: any): Promise<any[]> {
  const outline = await pdf.getOutline();
  if (!outline || !Array.isArray(outline)) return [];

  async function resolveItem(item: any): Promise<any> {
    let page: number | null = null;
    try {
      let dest = item?.dest;
      let destArray: any = null;
      if (typeof dest === 'string') {
        // named destination; resolve to explicit dest array
        try { destArray = await pdf.getDestination(dest); } catch (e) { destArray = null; }
      } else if (Array.isArray(dest)) {
        destArray = dest;
      }

      if (Array.isArray(destArray) && destArray.length > 0) {
        const first = destArray[0];
        try {
          // If first is a ref to a page object, getPageIndex resolves it to 0-based index
          const idx = await (pdf as any).getPageIndex(first);
          if (Number.isFinite(idx)) page = idx + 1;
        } catch (e) {
          // fallback: sometimes first may already be a page number
          if (typeof first === 'number') page = first;
        }
      }
    } catch (e) {
      // ignore resolution errors
      page = null;
    }

    const children: any[] = [];
    if (Array.isArray(item.items) && item.items.length > 0) {
      for (const it of item.items) {
        children.push(await resolveItem(it));
      }
    }

    return { title: item?.title ?? '', page, url: item?.url ?? null, items: children };
  }

  const out: any[] = [];
  for (const it of outline) out.push(await resolveItem(it));
  return out;
}

type ChapterRow = {
  name: string;
  printedStart?: number | null;
  printedEnd?: number | null;
  alignedStart?: number | null;
  alignedEnd?: number | null;
  // optional nested subchapters when provided by Gemini (JSON-assisted mode)
  subchapters?: ChapterRow[];
};

type ChunkItem = {
  chapterIndex: number;
  chapterName: string;
  page: number;
  text: string;
  // For university mode: optional parent chapter index and subchapter index
  parentChapterIndex?: number | null;
  subchapterIndex?: number | null;
  parentChapterTitle?: string | null;
  subchapterTitle?: string | null;
};

const RagSetup: React.FC = () => {
  const { role, token } = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [bookName, setBookName] = useState<string>('');
  const [author, setAuthor] = useState<string>('');
  const [filePages, setFilePages] = useState<number | null>(null);
  const [thumbs, setThumbs] = useState<string[]>([]);
  const [showFullPreview, setShowFullPreview] = useState<boolean>(false);
  const [tocMethod, setTocMethod] = useState<'gemini' | 'bookmarks' | 'json'>('gemini');
  // Auto-match score (percent) per chapter index; a mapped chapter without one was picked by hand.
  const [autoScores, setAutoScores] = useState<Record<number, number>>({});
  const [embedStartedAt, setEmbedStartedAt] = useState<number | null>(null);
  const [grades, setGrades] = useState<Array<any>>([]);
  const [subjects, setSubjects] = useState<Array<any>>([]);
  const [selectedGrade, setSelectedGrade] = useState<number | null>(null);
  const [selectedSubject, setSelectedSubject] = useState<number | null>(null);
  // Institution mode: 'school' uses grade->subject, 'university' uses field->year->course
  const [institutionMode, setInstitutionMode] = useState<'school' | 'university'>('school');
  const [fields, setFields] = useState<Array<{ id: number; name: string }>>([]);
  const [fieldsLoading, setFieldsLoading] = useState<boolean>(false);
  const [selectedField, setSelectedField] = useState<number | null>(null);
  const [academicYears, setAcademicYears] = useState<Array<{ id: number; name: string }>>([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState<number | null>(null);
  const [courses, setCourses] = useState<Array<{ id: number; name: string }>>([]);
  const [selectedCourse, setSelectedCourse] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // New state for Gemini and TOC input
  const [apiKey, setApiKey] = useState<string>('');
  const [tocStart, setTocStart] = useState<string>('');
  const [tocEnd, setTocEnd] = useState<string>('');
  const [useImageMode, setUseImageMode] = useState<boolean>(false);
  // JSON-assisted mode: when enabled (university admins), user uploads a companion JSON
  // describing chapters/subchapters. Gemini will be asked to map those entries to PDF pages.
  const [useJsonMode, setUseJsonMode] = useState<boolean>(false);
  const [jsonFile, setJsonFile] = useState<File | null>(null);
  const [directJsonFile, setDirectJsonFile] = useState<File | null>(null);
  const [sendBookmarkTree, setSendBookmarkTree] = useState<boolean>(false);
  const [pagesPerUpload, setPagesPerUpload] = useState<string>('4');
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ name: string; uri?: string; mimeType?: string; state?: string; error?: string; blob?: Blob; start?: number; end?: number }>>([]);
  const [uploadConcurrency, setUploadConcurrency] = useState<number>(2);
  const [chunkModeChoice, setChunkModeChoice] = useState<'text' | 'image'>('text');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [confirmLeave, setConfirmLeave] = useState<boolean>(false);
  const [useStaging, setUseStaging] = useState<boolean>(false);

  // Chapters parsed from Gemini and alignment handling
  const [chapters, setChapters] = useState<ChapterRow[]>([]);
  // Both chapter tables (page ranges, mapping) show 10 chapters at a time; edits stay keyed by the real chapter index.
  const rangePg = usePagination(chapters, 10);
  const mapPg = usePagination(chapters, 10);
  // The user enters which PDF page corresponds to printed page 1 (e.g., if printed page 1 is on PDF page 7, enter 7)
  const [alignBaseInput, setAlignBaseInput] = useState<string>('');
  const [lastChapterEndInput, setLastChapterEndInput] = useState<string>('');
  // Wizard state
  const [currentStep, setCurrentStep] = useState<number>(1); // 1: select PDF, 2: preview + TOC, 3: chapters + align
  const [showStep3Preview, setShowStep3Preview] = useState<boolean>(false);
  const [alignmentNotice, setAlignmentNotice] = useState<string | null>(null);
  const [forceRerender, setForceRerender] = useState<number>(0);

  // Debug: log when chapters state changes so we can confirm UI gets updated
  useEffect(() => {
    try {
      
      // ensure a render tick occurs so controlled inputs refresh
      setForceRerender((n) => n + 1);
    } catch (e) {
      
    }
  }, [chapters]);
  // Mapping state: DB chapters for selected subject and user-selected mappings
  const [dbChapters, setDbChapters] = useState<Array<any>>([]);
  // For university flow: course-level subchapters fetched separately
  const [courseSubchapters, setCourseSubchapters] = useState<Array<{ id: number; name: string }>>([]);
  // mapping for gemini-provided subchapters: key `${chapterIdx}_${subIdx}` -> db subchapter id or null
  const [subchapterMappings, setSubchapterMappings] = useState<Record<string, number | null>>({});
  const [chapterMappings, setChapterMappings] = useState<Record<number, number | null>>({});
  // Chunking state
  const [isChunking, setIsChunking] = useState<boolean>(false);
  const [chunkProgress, setChunkProgress] = useState<{ completed: number; total: number; label: string }>({ completed: 0, total: 0, label: '' });
  const [chunkSummaries, setChunkSummaries] = useState<{ name: string; start: number; end: number; chunks: number }[]>([]);
  const [chunkItems, setChunkItems] = useState<ChunkItem[]>([]);
  // Embedding state
  const [isEmbedding, setIsEmbedding] = useState<boolean>(false);
  const [embedProgress, setEmbedProgress] = useState<{ completed: number; total: number; label: string }>({ completed: 0, total: 0, label: '' });
  const [embedSummaries, setEmbedSummaries] = useState<{ chapterName: string; count: number }[]>([]);
  // Store embedding details (per-chapter) so we can preview embeddings after run
  const [embedDetails, setEmbedDetails] = useState<Record<string, Array<{ page: number; text: string; raw: any }>>>({});
  const [openPreviewChapters, setOpenPreviewChapters] = useState<Record<string, boolean>>({});
  const [showApiKeyModal, setShowApiKeyModal] = useState<boolean>(false);
  const [isSavingKey, setIsSavingKey] = useState<boolean>(false);

  // Normalize embedding response items into a small preview object so UI can show something
  function normalizeEmbeddingItem(item: any) {
    if (!item) return { note: 'no data' };
    // common shapes: { embedding: number[] } or { embeddings: [...]} or raw array
    let arr: any[] | null = null;
    if (Array.isArray(item) && item.length > 0 && typeof item[0] === 'number') arr = item as any[];
    else if (Array.isArray(item?.values)) arr = item.values;
    else if (Array.isArray(item?.embedding)) arr = item.embedding;
    else if (Array.isArray(item?.vector)) arr = item.vector;
    else if (Array.isArray(item?.output?.embedding)) arr = item.output.embedding;
    else if (Array.isArray(item?.embeddings)) {
      // embeddings can be an array of numbers or an array of objects with .values/.embedding/.vector
      const first = item.embeddings[0];
      if (Array.isArray(first)) arr = first;
      else if (Array.isArray(first?.values)) arr = first.values;
      else if (Array.isArray(first?.embedding)) arr = first.embedding;
      else if (Array.isArray(first?.vector)) arr = first.vector;
      else {
        // fallback: if embeddings is already numeric array (rare), use it
        if (item.embeddings.every((el: any) => typeof el === 'number')) arr = item.embeddings;
      }
    }
    else if (Array.isArray(item?.data)) {
      // sometimes data is an array containing embedding objects
      const cand = item.data.find((d:any) => Array.isArray(d?.embedding) || Array.isArray(d?.values) || Array.isArray(d?.vector));
      if (cand) arr = cand.embedding ?? cand.values ?? cand.vector ?? null;
    }
    if (arr && Array.isArray(arr)) {
      return { dim: arr.length, sample: arr.slice(0, 6), embedding_vector: arr };
    }
    // If there is a numeric field count or shape metadata
    if (typeof item?.length === 'number') return { dim: item.length, sample: Array.isArray(item) ? item.slice(0, 6) : undefined };
    // Fallback: return small object with some known properties
    const keys = Object.keys(item || {}).slice(0, 6);
    const small: any = { note: 'unrecognized shape', keys };
    return small;
  }

  useEffect(() => {
    // cleanup object URL on unmount or file change
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // Fetch grades for the user's institution when token is available
  useEffect(() => {
    const loadGrades = async () => {
      if (!token) return;
      try {
        try {
          const data = await request('/api/school/grades/', { headers: { 'Authorization': `Bearer ${token}` } })
          // data.grades expected
          setGrades(Array.isArray(data?.grades) ? data.grades : [])
        } catch (e) {
          // ignore
        }
      } catch (e) {
        
      }
    };
    loadGrades();
  }, [token]);

  // Load fields of study for institution if switching to university mode
  useEffect(() => {
    const loadFields = async () => {
      if (!token) return;
      if (institutionMode !== 'university') return;
      setFieldsLoading(true);
      try {
        // Need institution id from user info - fetch user info endpoint
        let instId: any = null
        try {
          const ud = await request('/api/user-info/', { headers: { Authorization: token ? `Bearer ${token}` : '' } })
          instId = ud?.institution_id
        } catch (e) {
          setFields([])
          setFieldsLoading(false)
          return
        }
        let fetched: Array<any> = [];
        if (!instId || role === 'superuser') {
          // Superuser should be able to load all fields across institutions
          fetched = await (await import('../../utils/remote')).adminGetAllFields();
          // normalize to id/name shape
          fetched = (fetched || []).map((f: any) => ({ id: f.id, name: f.name }));
        } else {
          fetched = await (await import('../../utils/remote')).adminGetInstitutionFields(instId);
        }
        setFields(fetched || []);
      } catch (e) {
        
        setFields([]);
      } finally {
        setFieldsLoading(false);
      }
    };
    loadFields();
  }, [token, institutionMode]);

  // When selectedField changes, load academic years
  useEffect(() => {
    const loadYears = async () => {
      if (!token || !selectedField) {
        setAcademicYears([]);
        setSelectedAcademicYear(null);
        return;
      }
      try {
        let instId: any = null
        try {
          const ud = await request('/api/user-info/', { headers: { Authorization: token ? `Bearer ${token}` : '' } })
          instId = ud?.institution_id
        } catch (e) {
          return
        }
        if (!instId) return;
        const years = await (await import('../../utils/remote')).adminGetFieldAcademicYears(instId, selectedField);
        setAcademicYears(years || []);
      } catch (e) {
        
        setAcademicYears([]);
      }
    };
    loadYears();
  }, [token, selectedField]);

  // When academic year changes, load courses
  useEffect(() => {
    const loadCourses = async () => {
      if (!token || !selectedAcademicYear || !selectedField) {
        setCourses([]);
        setSelectedCourse(null);
        return;
      }
      try {
        let instId: any = null
        try {
          const ud = await request('/api/user-info/', { headers: { Authorization: token ? `Bearer ${token}` : '' } })
          instId = ud?.institution_id
        } catch (e) {
          return
        }
        if (!instId) return;
        const cs = await (await import('../../utils/remote')).adminGetAcademicYearCourses(instId, selectedField!, selectedAcademicYear);
        setCourses(cs || []);
      } catch (e) {
        
        setCourses([]);
      }
    };
    loadCourses();
  }, [token, selectedAcademicYear, selectedField]);

  // When selectedGrade changes, fetch subjects for that grade
  useEffect(() => {
    const loadSubjects = async () => {
      if (!token || !selectedGrade) {
        setSubjects([]);
        return;
      }
      try {
        try {
          const data = await request(`/api/school/grades/${selectedGrade}/subjects/`, { headers: { 'Authorization': `Bearer ${token}` } })
          setSubjects(Array.isArray(data?.subjects) ? data.subjects : [])
        } catch (e) {
          setSubjects([])
        }
      } catch (e) {
        
        setSubjects([]);
      }
    };
    loadSubjects();
  }, [token, selectedGrade]);

  // When selectedSubject changes, fetch DB chapters for mapping step
  useEffect(() => {
    const loadDbChapters = async () => {
      // Only load DB chapters when we have both a selected subject AND a selected grade
      // Do not load subject chapters when operating in university mode or when the user is a university admin
      if (role === 'college_university' || institutionMode === 'university') {
        // In university mode, load course chapters (which include subchapters) when a course is selected
        if (!token || !selectedCourse) {
          setDbChapters([]);
          setCourseSubchapters([]);
          setChapterMappings({});
          return;
        }
        try {
          const remote = await import('../../utils/remote');
          const resp = await remote.getCourseChapters(token ? `Bearer ${token}` : null, selectedCourse);
          const list = Array.isArray(resp?.chapters) ? resp.chapters : [];
          setDbChapters(list.map((c:any) => ({ id: c.id, name: c.name })));
          // collect subchapters for the selected course (flattened)
          const subs: Array<{ id: number; name: string }> = [];
          for (const ch of list) {
            if (Array.isArray(ch.subchapters)) {
              for (const s of ch.subchapters) subs.push({ id: s.id, name: s.name });
            }
          }
          setCourseSubchapters(subs);
        } catch (e) {
          
          setDbChapters([]);
          setCourseSubchapters([]);
        }
        // initialize mappings if chapters already exist: null for each parsed chapter
        setChapterMappings((prev) => {
          const next: Record<number, number | null> = {};
          (chapters || []).forEach((_, idx) => { next[idx] = prev[idx] ?? null; });
          return next;
        });
        return;
      }
      if (!token || !selectedSubject || !selectedGrade) {
        setDbChapters([]);
        setChapterMappings({});
        return;
      }
      try {
        // Use the subject-only chapters endpoint (server returns chapters with school_grade_ids).
        // We avoid issuing a grade/subject-scoped request to prevent noisy 404s when that endpoint
        // is not present on the backend. We will filter client-side by selectedGrade below.
        let data: any = null
        try {
          data = await request(`/api/school/subjects/${selectedSubject}/chapters/`, { headers: { 'Authorization': `Bearer ${token}` } })
        } catch (e) {
          setDbChapters([])
          setChapterMappings({})
          return
        }
        const list = Array.isArray(data?.chapters) ? data.chapters : [];
  // Only keep chapters that are associated with the selected grade (school_grade_ids contains selectedGrade)
  const filtered = list.filter((c: any) => Array.isArray(c?.school_grade_ids) && c.school_grade_ids.includes(selectedGrade));
  setDbChapters(filtered);

        // initialize mappings if chapters already exist: null for each parsed chapter
        setChapterMappings((prev) => {
          const next: Record<number, number | null> = {};
          (chapters || []).forEach((_, idx) => { next[idx] = prev[idx] ?? null; });
          return next;
        });
      } catch (e) {
        
        setDbChapters([]);
        setChapterMappings({});
      }
    };
    loadDbChapters();
  }, [token, selectedSubject, selectedGrade, chapters]);

  // On mount / token change: try to fetch saved API key for user
  useEffect(() => {
    const tryFetchKey = async () => {
      if (!token) return;
      try {
        try {
          const data = await request('/api/user/gemini-key/', { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } })
          if (data?.has_key && data?.api_key) {
            setApiKey(data.api_key || '');
          } else {
            setShowApiKeyModal(true);
          }
        } catch (e) {
          // ignore network errors; user can manually paste key
          
        }
      } catch (e) {
        // double-catch (keeps structure similar)
      }
    };
    tryFetchKey();
  }, [token]);

  // Save API key to server
  const saveApiKeyToServer = async (key: string) => {
    if (!token) return false;
    setIsSavingKey(true);
    try {
      await request('/api/user/gemini-key/', { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ api_key: key }) })
      setIsSavingKey(false);
      setShowApiKeyModal(false);
      setApiKey(key);
      return true;
    } catch (e) {
      
      setIsSavingKey(false);
      return false;
    }
  };

  function toIntOrNull(v: any): number | null {
    if (v === null || v === undefined) return null;
    const num = parseInt(String(v).replace(/[^0-9-]/g, ''), 10);
    return Number.isFinite(num) ? num : null;
  }

  function parseChaptersFromOutput(raw: any, base?: number): ChapterRow[] {
    const arr: any[] = Array.isArray(raw) ? raw : [];
    const baseNum = typeof base === 'number' && Number.isFinite(base) && base > 0 ? base : undefined;

    // Recursive helper to parse a single item possibly containing nested subchapters
    function parseItem(it: any): ChapterRow {
      const name = (it?.name ?? '').toString();
      const printedStart = toIntOrNull((it && (it['start=page'] ?? it.start_page ?? it.start)) as any);
      const printedEnd = toIntOrNull((it && (it['end-age'] ?? it.end_page ?? it.end)) as any);
      const alignedStart = baseNum && printedStart != null ? baseNum + printedStart - 1 : (it?.alignedStart ?? null);
      const alignedEnd = baseNum && printedEnd != null ? baseNum + printedEnd - 1 : (it?.alignedEnd ?? null);

      // Detect subchapter arrays with common keys
      const rawSubs = it?.subchapters ?? it?.sub_chapters ?? it?.children ?? it?.parts ?? [];
      const subchapters = Array.isArray(rawSubs) && rawSubs.length > 0 ? rawSubs.map((s: any) => parseItem(s)) : undefined;

      return { name, printedStart, printedEnd, alignedStart, alignedEnd, subchapters } as ChapterRow;
    }

    return arr.map((it) => parseItem(it));
  }

  async function applyAlignment(baseNum: number) {
    // Apply numeric base alignment and ensure the last chapter's aligned end
    // becomes the PDF's last page when the end is missing/invalid.
    
    setError(null);
    if (!Number.isFinite(baseNum) || baseNum <= 0) {
      
      return;
    }
    // If we don't have a file, just apply the base offset without total-page fixes
    if (!file) {
      setChapters((prev) => prev.map((row) => ({
        ...row,
        // Option 1: treat existing alignedStart/End as printed page numbers when printedStart isn't present
        alignedStart: row.printedStart != null ? baseNum + row.printedStart - 1 : (row.alignedStart != null ? baseNum + row.alignedStart - 1 : null),
        alignedEnd: row.printedEnd != null ? baseNum + row.printedEnd - 1 : (row.alignedEnd != null ? baseNum + row.alignedEnd - 1 : null),
      })));
      return;
    }

    // Load PDF to determine total pages so we can set the last chapter end correctly
    try {
      const pdf = await loadPdfDocument(file);
      const totalPages = Number(pdf.numPages) || 0;

  setChapters((prev) => {
        const lastIndex = prev.length - 1;

        // recursive helper to align a subchapter (and its nested subchapters)
        function alignSub(sub: ChapterRow): ChapterRow {
          // Option 1: If printedStart exists, use it; otherwise treat existing alignedStart as printed and convert.
          let sStart = sub.printedStart != null ? baseNum + sub.printedStart - 1 : (sub.alignedStart != null ? baseNum + sub.alignedStart - 1 : null);
          let sEnd = sub.printedEnd != null ? baseNum + sub.printedEnd - 1 : (sub.alignedEnd != null ? baseNum + sub.alignedEnd - 1 : null);
          if (sEnd != null && totalPages > 0) sEnd = Math.min(sEnd, totalPages);
          if (sStart != null) sStart = Math.max(1, sStart);
          let nested: ChapterRow[] | undefined = undefined;
          if (Array.isArray(sub.subchapters) && sub.subchapters.length > 0) {
            nested = sub.subchapters.map((ss) => alignSub(ss));
          }
          return { ...sub, alignedStart: sStart, alignedEnd: sEnd, subchapters: nested } as ChapterRow;
        }

        const updated = prev.map((row, idx) => {
          // Align the chapter itself
          // Option 1: If printedStart exists, use it; otherwise treat existing alignedStart as printed and convert.
          let alignedStart = row.printedStart != null ? baseNum + row.printedStart - 1 : (row.alignedStart != null ? baseNum + row.alignedStart - 1 : null);
          let alignedEnd = row.printedEnd != null ? baseNum + row.printedEnd - 1 : (row.alignedEnd != null ? baseNum + row.alignedEnd - 1 : null);

          // If this is the last chapter and we have a PDF, always set its alignedEnd
          // to the last PDF page so the chapter covers until the end of the book.
          if (idx === lastIndex && alignedStart != null) {
            alignedEnd = totalPages > 0 ? totalPages : alignedEnd;
          }

          // Clamp values into PDF bounds for chapter
          if (alignedEnd != null && totalPages > 0) alignedEnd = Math.min(alignedEnd, totalPages);
          if (alignedStart != null) alignedStart = Math.max(1, alignedStart);

          // Align nested subchapters recursively
          let newSubs: ChapterRow[] | undefined = undefined;
          if (Array.isArray(row.subchapters) && row.subchapters.length > 0) {
            newSubs = row.subchapters.map((sub) => alignSub(sub));
          }

          // return aligned chapter row
          return { ...row, alignedStart, alignedEnd, subchapters: newSubs } as ChapterRow;
        });

        // commit updated chapters
        return updated;
      });
    } catch (e) {
      // Fallback: apply alignment without total page knowledge
      setChapters((prev) => prev.map((row) => ({
        ...row,
        // Option 1: when printedStart not present, treat existing alignedStart as printed and convert
        alignedStart: row.printedStart != null ? baseNum + row.printedStart - 1 : (row.alignedStart != null ? baseNum + row.alignedStart - 1 : null),
        alignedEnd: row.printedEnd != null ? baseNum + row.printedEnd - 1 : (row.alignedEnd != null ? baseNum + row.alignedEnd - 1 : null),
      })));
    }
  }

  // Set the alignedEnd of the last chapter to a specific PDF page number
  const setLastChapterEnd = async (override?: number) => {
    setError(null);
    const p = override ?? parseInt(lastChapterEndInput, 10);
    if (!Number.isFinite(p) || p <= 0) {
      setError('Please enter a valid PDF page number (>= 1) for the last chapter end.');
      return;
    }

    let totalPages = 0;
    if (file) {
      try {
        const pdf = await loadPdfDocument(file);
        totalPages = Number(pdf.numPages) || 0;
        try { await pdf.destroy?.(); } catch {}
      } catch (e) {
        
      }
    }

    const clamped = totalPages > 0 ? Math.min(Math.max(1, p), totalPages) : Math.max(1, p);

    setChapters((prev) => {
      if (!prev || prev.length === 0) return prev;
      const lastIdx = prev.length - 1;
      const updated = prev.map((r, i) => i === lastIdx ? { ...r, alignedEnd: clamped } : r);
      
      return updated;
    });
    // force re-render to ensure UI updates
    setForceRerender((n) => n + 1);
    setAlignmentNotice('Last chapter end updated');
    setTimeout(() => setAlignmentNotice(null), 3000);
  };

  // Create a new PDF Blob containing only pages [startPage..endPage] (1-based)
  async function createPdfForRange(file: File, startPage: number, endPage: number): Promise<Blob> {
    const arrayBuffer = await file.arrayBuffer();
    const srcDoc = await PDFDocument.load(arrayBuffer);
    const dstDoc = await PDFDocument.create();
    const total = srcDoc.getPageCount();
    const s = Math.max(1, Math.min(startPage, total));
    const e = Math.max(s, Math.min(endPage, total));
    // copyPages uses zero-based indices
    const indices = [] as number[];
    for (let i = s - 1; i <= e - 1; i++) indices.push(i);
    const pages = await dstDoc.copyPages(srcDoc, indices);
    pages.forEach((page: any) => dstDoc.addPage(page));
    const bytes = await dstDoc.save();
    // Ensure we pass an ArrayBuffer or Uint8Array to Blob
    const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    // cast buffer to ArrayBuffer to satisfy TS in environments with ArrayBufferLike
    return new Blob([u8.buffer as unknown as ArrayBuffer], { type: 'application/pdf' });
  }

    // (Bookmark tree logging helper removed; replaced by checkbox-driven send behavior)

  // Image-based chunking: for each chapter range, create per-page or per-chunk PDF(s), upload to Gemini,
  // ask Gemini to extract all text on the page(s) and return JSON like [{page: '1', text: '...'}, ...]
  async function handleChunkChaptersImageMode() {
    if (!file) { setError('Please choose a PDF file first.'); return; }
    if (!chapters || chapters.length === 0) { setError('No chapters to chunk.'); return; }
    setIsChunking(true);
    try {

      // Prepare ranges from chapters (alignedStart..alignedEnd) and create PDF blobs split by pagesPerUpload
      const per = Math.max(1, parseInt(pagesPerUpload || '1', 10));
  // no need to load full PDF here; createPdfForRange will handle page extraction as needed

      // Build an array of page ranges to process (chapter by chapter)
      const chapterRanges = (chapters || []).map((c, idx) => ({ idx, name: c.name || `Chapter ${idx+1}`, start: Number(c.alignedStart || 0), end: Number(c.alignedEnd || 0) })).filter(r => r.start>0 && r.end>=r.start);
      let allPagesToProcess: { chapterIdx: number; page: number }[] = [];
      for (const cr of chapterRanges) {
        for (let p = cr.start; p <= cr.end; p++) allPagesToProcess.push({ chapterIdx: cr.idx, page: p });
      }
      if (allPagesToProcess.length === 0) { setError('No pages found to process. Ensure chapters are aligned.'); setIsChunking(false); return; }

      setChunkProgress({ completed: 0, total: allPagesToProcess.length, label: 'Preparing uploads…' });

      // Group pages into groups of `per` and upload each group as a PDF
      const groups: { pages: number[]; chapterIdxs: number[] }[] = [];
      for (let i = 0; i < allPagesToProcess.length; i += per) {
        const slice = allPagesToProcess.slice(i, i + per);
        const pages = slice.map(s => s.page);
        const chapterIdxs = Array.from(new Set(slice.map(s => s.chapterIdx)));
        groups.push({ pages, chapterIdxs });
      }

      let processed = 0;
      const newChunkItems: ChunkItem[] = [];

      // Create blobs for every group first
      const groupsWithBlobs: Array<{ name: string; blob: Blob; start: number; end: number }> = [];
      for (let gi = 0; gi < groups.length; gi++) {
        const g = groups[gi];
        const gStart = g.pages[0];
        const gEnd = g.pages[g.pages.length - 1];
        const blob = await createPdfForRange(file, gStart, gEnd);
        groupsWithBlobs.push({ name: `pages_${gStart}-${gEnd}.pdf`, blob, start: gStart, end: gEnd });
      }

      // register uploadedFiles entries
      setUploadedFiles(groupsWithBlobs.map((b) => ({ name: b.name, state: 'PENDING', blob: b.blob, start: b.start, end: b.end })));

      // Process each group via backend (base64 PDF + aiBatchGenerate)
      for (let i = 0; i < groupsWithBlobs.length; i++) {
        const g = groupsWithBlobs[i];
        setUploadedFiles((prev) => prev.map((f) => (f.name === g.name ? { ...f, state: 'UPLOADING' } : f)));

        const extractPrompt = `Extract all visible text from the uploaded PDF file. For each PDF page return an object with keys {"page": "<page number>", "text": "<extracted text>"}. Return ONLY a JSON array like [{"page":"1","text":"..."}, ...]. Page numbers should refer to the PDF page number within the original book. Return ONLY valid JSON. Do not include any introductory text, concluding text, or markdown formatting like \`\`\`json.`;
        setChunkProgress({ completed: processed, total: allPagesToProcess.length, label: `Requesting text extraction for pages ${g.start}-${g.end}…` });

        // Convert blob to base64
        const arrayBuffer = await g.blob.arrayBuffer();
        const base64Data = btoa(
          new Uint8Array(arrayBuffer)
            .reduce((data: string, byte: number) => data + String.fromCharCode(byte), '')
        );

        let genResp: any = null;
        try {
          genResp = await aiBatchGenerate({
            toBeValidated: token,
            messages: [{ role: 'user', content: extractPrompt }],
            model: 'gemini-3.1-flash-lite',
            images: [{ mime_type: 'application/pdf', data: base64Data }],
          });
          setUploadedFiles((prev) => prev.map((f) => (f.name === g.name ? { ...f, state: 'READY' } : f)));
        } catch (genErr: any) {
          
          setUploadedFiles((prev) => prev.map((f) => (f.name === g.name ? { ...f, state: 'FAILED', error: genErr?.message || String(genErr) } : f)));
          continue;
        }
        const out = genResp?.text ?? '';
        // Parse JSON array and map sequentially to the group's original pages when explicit page fields are missing.
        try {
          const jsonStart = out.indexOf('[');
          const jsonEnd = out.lastIndexOf(']');
          const jsonStr = jsonStart >= 0 && jsonEnd >= 0 ? out.slice(jsonStart, jsonEnd + 1) : out;
          const parsed: any[] = JSON.parse(jsonStr || '[]');

          // Always map parsed array entries sequentially to the group's original pages.
          // parsed[0] -> g.start, parsed[1] -> g.start + 1, etc. (Ignore Gemini-provided page numbers.)
          for (let idxParsed = 0; idxParsed < parsed.length; idxParsed++) {
            const obj = parsed[idxParsed];
            const text = (obj?.text ?? obj?.content ?? '').toString();
            const pnum = g.start + idxParsed; // sequential mapping

            // create chunk item for the correct chapter (find chapter that contains this page)
            const chapterIndex = (chapters || []).findIndex(ch => Number(ch.alignedStart) <= pnum && Number(ch.alignedEnd) >= pnum);
            const chName = chapterIndex >= 0 ? (chapters || [])[chapterIndex].name : `Unknown`;
            newChunkItems.push({ chapterIndex: chapterIndex >= 0 ? chapterIndex : 0, chapterName: chName, page: pnum, text });
            processed += 1;
            setChunkProgress({ completed: processed, total: allPagesToProcess.length, label: `Processed page ${pnum}` });
          }
        } catch (parseErr) {
          
          setUploadedFiles((prev) => prev.map((f) => (f.name === g.name ? { ...f, state: 'FAILED', error: 'Invalid JSON from Gemini' } : f)));
          // do not abort the whole run; continue to next file
          continue;
        }
      }

      // merge newChunkItems into existing chunkItems state and summaries
      setChunkItems(newChunkItems);
      // build summaries
      const summaryMap = new Map<string, { name: string; start: number; end: number; chunks: number }>();
      for (const ci of newChunkItems) {
        const key = ci.chapterName;
        const cur = summaryMap.get(key) ?? { name: key, start: ci.page, end: ci.page, chunks: 0 };
        cur.start = Math.min(cur.start, ci.page);
        cur.end = Math.max(cur.end, ci.page);
        cur.chunks += 1;
        summaryMap.set(key, cur);
      }
      setChunkSummaries(Array.from(summaryMap.values()));
      setChunkProgress({ completed: processed, total: allPagesToProcess.length, label: 'Completed' });
    } catch (e: any) {
      
      setError(e?.message || 'Failed to perform image-based chunking.');
    } finally {
      setIsChunking(false);
    }
  }

  // Upload a file blob to Gemini via GoogleGenAI client and poll until processed (or failed)
  async function uploadFileToGemini(ai: any, blob: Blob, displayName: string) {
    try {
      const file = await ai.files.upload({ file: blob, config: { displayName } });
      let getFile = await ai.files.get({ name: file.name });
      // poll until processing completes
      while (getFile.state === 'PROCESSING') {
        // update status
        setUploadedFiles((prev) => prev.map((f) => (f.name === file.name ? { ...f, state: 'PROCESSING' } : f)));
        await new Promise((resolve) => setTimeout(resolve, 2500));
        getFile = await ai.files.get({ name: file.name });
      }
      if (getFile.state === 'FAILED') {
        setUploadedFiles((prev) => prev.map((f) => (f.name === file.name ? { ...f, state: 'FAILED', error: 'File processing failed' } : f)));
        throw new Error('File processing failed for ' + file.name);
      }
      // mark ready
      setUploadedFiles((prev) => prev.map((f) => (f.name === file.name ? { ...f, state: getFile.state, uri: getFile.uri, mimeType: getFile.mimeType } : f)));
      return getFile;
    } catch (e: any) {
      // ensure we mark the file as failed if present
      setUploadedFiles((prev) => prev.map((f) => (f.name === displayName ? { ...f, state: 'FAILED', error: e?.message || String(e) } : f)));
      throw e;
    }
  }

  // Upload multiple files with a concurrency limit
  async function uploadFilesWithConcurrency(ai: any, entries: Array<{ name: string; blob: Blob; start?: number; end?: number }>, concurrency: number) {
    const results: Array<any> = new Array(entries.length);
    let idx = 0;
    const runner = async () => {
      while (true) {
        const i = idx++;
        if (i >= entries.length) return;
        const ent = entries[i];
        // mark uploading
        setUploadedFiles((prev) => prev.map((f) => (f.name === ent.name ? { ...f, state: 'UPLOADING' } : f)));
        try {
          const res = await uploadFileToGemini(ai, ent.blob, ent.name);
          results[i] = res;
        } catch (e) {
          results[i] = null;
        }
      }
    };
    const workers = [] as Promise<any>[];
    for (let w = 0; w < Math.max(1, concurrency); w++) workers.push(runner());
    await Promise.all(workers);
    return results;
  }

  // Retry upload for a specific file entry (must have blob)
  const retryUpload = async (entryName: string) => {
    const ent = uploadedFiles.find((f) => f.name === entryName && f.blob);
    if (!ent || !ent.blob) return;
    try {
      setUploadedFiles((prev) => prev.map((f) => (f.name === entryName ? { ...f, state: 'RETRYING', error: undefined } : f)));

      // Convert blob to base64 and send via backend
      const arrayBuffer = await ent.blob.arrayBuffer();
      const base64Data = btoa(
        new Uint8Array(arrayBuffer)
          .reduce((data: string, byte: number) => data + String.fromCharCode(byte), '')
      );

      const extractPrompt = `Extract all visible text from the uploaded PDF file. For each PDF page return an object with keys {"page": "<page number>", "text": "<extracted text>"}. Return ONLY a JSON array like [{"page":"1","text":"..."}, ...]. Page numbers should refer to the PDF page number within the original book. Return ONLY valid JSON. Do not include any introductory text, concluding text, or markdown formatting like \`\`\`json.`;
      setUploadedFiles((prev) => prev.map((f) => (f.name === entryName ? { ...f, state: 'EXTRACTING' } : f)));

      let genResp: any = null;
      try {
        genResp = await aiBatchGenerate({
          toBeValidated: token,
          messages: [{ role: 'user', content: extractPrompt }],
          model: 'gemini-3.1-flash-lite',
          images: [{ mime_type: 'application/pdf', data: base64Data }],
        });
      } catch (genErr: any) {
        
        setUploadedFiles((prev) => prev.map((f) => (f.name === entryName ? { ...f, state: 'FAILED', error: genErr?.message || String(genErr) } : f)));
        return;
      }

      const out = genResp?.text ?? '';
      // parse JSON and map sequentially to the entry's pages
      try {
        const jsonStart = out.indexOf('[');
        const jsonEnd = out.lastIndexOf(']');
        const jsonStr = jsonStart >= 0 && jsonEnd >= 0 ? out.slice(jsonStart, jsonEnd + 1) : out;
        const parsed: any[] = JSON.parse(jsonStr || '[]');

        // Prepare new chunk items for this entry
        const startPage = ent.start ?? 0;
        const newItems: ChunkItem[] = [];
        for (let idxParsed = 0; idxParsed < parsed.length; idxParsed++) {
          const obj = parsed[idxParsed];
          const text = (obj?.text ?? obj?.content ?? '').toString();
          const pnum = startPage + idxParsed;
          const chapterIndex = (chapters || []).findIndex(ch => Number(ch.alignedStart) <= pnum && Number(ch.alignedEnd) >= pnum);
          const chName = chapterIndex >= 0 ? (chapters || [])[chapterIndex].name : `Unknown`;
          newItems.push({ chapterIndex: chapterIndex >= 0 ? chapterIndex : 0, chapterName: chName, page: pnum, text });
        }

        // Merge into chunkItems state and update summaries/progress
        setChunkItems((prev) => {
          const merged = [...prev, ...newItems];
          // update summaries
          const summaryMap = new Map<string, { name: string; start: number; end: number; chunks: number }>();
          for (const ci of merged) {
            const key = ci.chapterName;
            const cur = summaryMap.get(key) ?? { name: key, start: ci.page, end: ci.page, chunks: 0 };
            cur.start = Math.min(cur.start, ci.page);
            cur.end = Math.max(cur.end, ci.page);
            cur.chunks += 1;
            summaryMap.set(key, cur);
          }
          setChunkSummaries(Array.from(summaryMap.values()));
          setChunkProgress((prevP) => ({ completed: merged.length, total: prevP.total, label: `Processed ${merged.length}/${prevP.total}` }));
          return merged;
        });

        // mark uploadedFiles entry as READY
        setUploadedFiles((prev) => prev.map((f) => (f.name === entryName ? { ...f, state: 'READY', error: undefined } : f)));
      } catch (parseErr) {
        
        setUploadedFiles((prev) => prev.map((f) => (f.name === entryName ? { ...f, state: 'FAILED', error: 'Invalid JSON from Gemini' } : f)));
        return;
      }
    } catch (e:any) {
      
        setUploadedFiles((prev) => prev.map((f) => (f.name === entryName ? { ...f, state: 'FAILED', error: e?.message || String(e) } : f)));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] || null;
    setError(null);
    if (!selected) {
      setFile(null);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      return;
    }
    // basic validation for PDF
    if (selected.type !== 'application/pdf' && !selected.name.toLowerCase().endsWith('.pdf')) {
      setError('Please select a valid PDF file.');
      setFile(null);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      return;
    }
    // set file and generate preview URL
    setFile(selected);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    const url = URL.createObjectURL(selected);
    setPreviewUrl(url);
    // auto-fill book name if empty
    if (!bookName.trim()) setBookName(selected.name.replace(/\.[^/.]+$/, ""));
  };

  const handleDirectJsonUpload = async () => {
    if (!directJsonFile) {
      setError('Please select a JSON file first.');
      return;
    }
    setIsProcessing(true);
    setError(null);
    try {
      const text = await directJsonFile.text();
      const raw = JSON.parse(text);
      
      const newChapters: ChapterRow[] = Object.entries(raw).map(([chapterName, chapterData]: [string, any]) => {
        const subchapters: ChapterRow[] | undefined = chapterData.subchapters 
          ? Object.entries(chapterData.subchapters).map(([subName, subData]: [string, any]) => ({
              name: subName,
              printedStart: toIntOrNull(subData.start_page),
              printedEnd: toIntOrNull(subData.end_page),
              alignedStart: toIntOrNull(subData.start_page),
              alignedEnd: toIntOrNull(subData.end_page),
            }))
          : undefined;

        return {
          name: chapterName,
          printedStart: toIntOrNull(chapterData.start_page),
          printedEnd: toIntOrNull(chapterData.end_page),
          alignedStart: toIntOrNull(chapterData.start_page),
          alignedEnd: toIntOrNull(chapterData.end_page),
          subchapters,
        };
      });

      setChapters(newChapters);
      setIsProcessing(false);
      setCurrentStep(3);
    } catch (err: any) {
      
      setError('Failed to parse JSON file. Please ensure it matches the required format.');
      setIsProcessing(false);
    }
  };

  // Log the PDF bookmark (outline) structure to the console for debugging
  const logBookmarkTree = async () => {
    setError(null);
    if (!file) {
      setError('Please choose a PDF file first to log its bookmark tree.');
      return;
    }
    try {
      const pdfDoc = await loadPdfDocument(file);
      const outline = await getPdfOutlineTree(pdfDoc);
      try { await pdfDoc.destroy?.(); } catch {}
      
      // also pretty-print for environments that support it
      try {
        
      } catch {}
    } catch (e: any) {
      
      setError('Failed to read PDF outline. See console for details.');
    }
  };

  // Normalize a string into words for matching
  function normalizeWords(s: string) {
    return (s || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter(Boolean);
  }

  // Find best match candidate by scoring common words and exact-phrase inclusion.
  // candidates: array of { id, name }
  function findBestMatch(title: string, candidates: Array<any>, minScore = 0.25) : { id: number | null; score: number } {
    const none = { id: null, score: 0 };
    if (!title || !Array.isArray(candidates) || candidates.length === 0) return none;
    const titleWords = normalizeWords(title);
    if (titleWords.length === 0) return none;

    let bestId: number | null = null;
    let bestScore = 0;

    const titlePhrase = title.trim().toLowerCase();

    for (const cand of candidates) {
      const candName = (cand?.name ?? cand?.title ?? '').toString();
      const candWords = normalizeWords(candName);
      if (candWords.length === 0) continue;

      // exact phrase match gets high score
      let score = 0;
      if (candName.toLowerCase().includes(titlePhrase) && titlePhrase.length > 2) score += 1.0;

      // word overlap score (fraction of title words found in candidate)
      const common = titleWords.filter(w => candWords.includes(w)).length;
      const overlap = common / titleWords.length;
      score += overlap; // overlap in [0,1]

      // penalize candidates that are much shorter/longer than title (naive)
      const lenDiff = Math.abs(candWords.length - titleWords.length);
      score = score - (lenDiff * 0.05);

      if (score > bestScore) {
        bestScore = score;
        bestId = cand.id ?? null;
      }
    }

    return bestScore >= minScore ? { id: bestId, score: bestScore } : none;
  }
  function findBestMatchId(title: string, candidates: Array<any>, minScore = 0.25) : number | null {
    return findBestMatch(title, candidates, minScore).id;
  }

  // Auto-match all generated chapters and (optionally) nested subchapters to DB chapters
  const autoMatchAll = () => {
    const newMap: Record<number, number | null> = {};
    const newSubMap: Record<string, number | null> = {};
    const newInfo: Record<number, number> = {};

    // match top-level chapters against dbChapters
    for (let idx = 0; idx < (chapters || []).length; idx++) {
      const c = chapters[idx];
      const best = findBestMatch(c.name || '', dbChapters || [], 0.25);
      newMap[idx] = best.id;
      // The name score is 1.0 for a full word overlap or an exact phrase; show it as a percentage, capped at 100.
      if (best.id != null) newInfo[idx] = Math.min(100, Math.round(best.score * 100));

      // match nested subchapters against courseSubchapters when in university mode
      if (institutionMode === 'university' && Array.isArray(c.subchapters)) {
        for (let sidx = 0; sidx < c.subchapters.length; sidx++) {
          const sc = c.subchapters[sidx];
          const key = `${idx}_${sidx}`;
          const bestSub = findBestMatchId(sc.name || '', courseSubchapters || [], 0.2);
          newSubMap[key] = bestSub;
        }
      }
    }

    setChapterMappings(newMap);
    setAutoScores(newInfo);
    if (Object.keys(newSubMap).length > 0) setSubchapterMappings(newSubMap);
  };

  const clearSelection = () => {
    setFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setError(null);
    // also clear the actual input value if needed
    const input = document.getElementById('pdfInput') as HTMLInputElement | null;
    if (input) input.value = '';
  };

  // "18.4 MB" / "612.0 KB"
  const fileSize = useMemo(() => {
    if (!file) return null;
    return file.size >= 1024 * 1024 ? `${(file.size / (1024 * 1024)).toFixed(1)} MB` : `${(file.size / 1024).toFixed(1)} KB`;
  }, [file]);
  const fileInfo = useMemo(() => {
    if (!file || !fileSize) return null;
    return filePages ? `${fileSize} · ${filePages} pages` : fileSize;
  }, [file, fileSize, filePages]);

  // The page count of the chosen PDF (for the file line, the step notes and "Set last end to page N").
  useEffect(() => {
    setFilePages(null);
    if (!file) return;
    let cancelled = false;
    (async () => {
      let pdf: any = null;
      try {
        pdf = await loadPdfDocument(file);
        const n = Number(pdf.numPages) || 0;
        if (!cancelled && n > 0) setFilePages(n);
      } catch { /* the count is a nicety; the wizard works without it */ }
      finally { try { await pdf?.destroy?.(); } catch { /* ignore */ } }
    })();
    return () => { cancelled = true; };
  }, [file]);

  // Step 2: thumbnails of pages 1-6 (design `.pgt`).
  useEffect(() => {
    setThumbs([]);
    if (!file || currentStep !== 2) return;
    let cancelled = false;
    (async () => {
      let pdf: any = null;
      try {
        pdf = await loadPdfDocument(file);
        const n = Math.min(6, Number(pdf.numPages) || 0);
        const urls: string[] = [];
        for (let p = 1; p <= n; p++) {
          const page = await pdf.getPage(p);
          const base = page.getViewport({ scale: 1 });
          const viewport = page.getViewport({ scale: 180 / base.width });
          const canvas = document.createElement('canvas');
          canvas.width = Math.ceil(viewport.width);
          canvas.height = Math.ceil(viewport.height);
          const ctx = canvas.getContext('2d');
          if (!ctx) break;
          await page.render({ canvas, canvasContext: ctx, viewport }).promise;
          urls.push(canvas.toDataURL('image/jpeg', 0.7));
          if (cancelled) return;
        }
        if (!cancelled) setThumbs(urls);
      } catch { if (!cancelled) setThumbs([]); }
      finally { try { await pdf?.destroy?.(); } catch { /* ignore */ } }
    })();
    return () => { cancelled = true; };
  }, [file, currentStep]);

  const handleContinue = async () => {
    setError(null);
    
    if (!file) {
      
      setError('Please choose a PDF file first.');
      return;
    }
    if (!apiKey.trim()) {
      
      // Prompt user to add key via modal instead of blocking inline input
      setShowApiKeyModal(true);
      setError('Please add your Gemini API key.');
      return;
    }
    const s = parseInt(tocStart, 10);
    const e = parseInt(tocEnd, 10);
    if (!Number.isFinite(s) || !Number.isFinite(e) || s <= 0 || e <= 0 || e < s) {
      
      setError('Please enter a valid Table of Contents page range (e.g., start 1, end 5; end >= start).');
      return;
    }

    try {
      setIsProcessing(true);

      // If JSON-assisted mapping is enabled, send the uploaded JSON + PDF page parts to Gemini
      if (useJsonMode) {
        
        if (!jsonFile) {
          
          setError('Please upload a companion JSON file when JSON-assisted mode is enabled.');
          setIsProcessing(false);
          return;
        }

        // read and validate JSON
        let jsonText = '';
        try {
          jsonText = await jsonFile.text();
          JSON.parse(jsonText); // validate JSON; content will be sent to Gemini
          
        } catch (je: any) {
          
          setError('Uploaded JSON file is not valid JSON.');
          setIsProcessing(false);
          return;
        }

        const per = Math.max(1, parseInt(pagesPerUpload || '4', 10));
        // If not in image mode, only extract text for the TOC pages and send that with the JSON.
        if (!useImageMode) {
          // 1) Extract text from the specified pages OR send bookmark tree JSON if requested
          
          // default prompt (text-based)
          let prompt = `You are given a JSON document that lists chapters and optional subchapters for a textbook. The JSON follows nested objects like {name: "...", subchapters: [{name: "..."}]}. Using the provided Table of Contents text (extracted from the PDF pages) determine for each chapter and subchapter the printed page numbers or PDF page numbers where they appear. Return ONLY a JSON array mirroring the input structure but with numeric fields start_page and end_page for each chapter and subchapter. For example: [{"name":"Unit 1","start_page":12,"end_page":34,"subchapters":[{"name":"Section A","start_page":12,"end_page":15}]}]. Do not include any extra text outside the JSON. Return ONLY valid JSON. Do not include any introductory text, concluding text, or markdown formatting like \`\`\`json.`;
          let tocTextOrOutline = '';
          if (sendBookmarkTree) {
            // load PDF and resolve outline
            try {
              const pdfDoc = await loadPdfDocument(file);
              const outline = await getPdfOutlineTree(pdfDoc);
              try { await pdfDoc.destroy?.(); } catch {}
              const outlineJson = JSON.stringify(outline || []);
              prompt = `You are given a companion JSON describing the expected chapter structure and a PDF bookmark (outline) JSON. The bookmark/outline JSON uses nested "items" (or "children") arrays to represent subchapters; treat those nested items as subchapters. Using the outline JSON, determine for each TOP-LEVEL chapter and for each nested subchapter (items) the PDF page numbers where they appear. Return ONLY a JSON array mirroring the input structure where each object has the exact keys: name (string), start_page (integer or null), end_page (integer or null), and subchapters (an array of child objects with the same shape). Always include the "subchapters" key (use an empty array when there are none). Preserve nesting depth. Do not include any extra text outside the JSON. Return ONLY valid JSON. Do not include any introductory text, concluding text, or markdown formatting like \`\`\`json.`;
              tocTextOrOutline = `OUTLINE_JSON:${outlineJson.slice(0,250000)}`;
            } catch (outlineErr) {
              
              setError('Failed to load PDF outline for JSON-assisted mapping.');
              setIsProcessing(false);
              return;
            }
          } else {
            try {
              tocTextOrOutline = await extractPdfTextRange(file, s, e);
            } catch (errText) {
              
              setError('Failed to extract TOC pages text for JSON-assisted mapping.');
              setIsProcessing(false);
              return;
            }
          }

          const contentText = `${prompt}\nJSON:${jsonText}\n${tocTextOrOutline.slice ? tocTextOrOutline.slice(0, 250000) : tocTextOrOutline}`;
          try {
            
            const genRespText: any = await aiBatchGenerate({
              toBeValidated: token,
              messages: [{ role: 'user', content: contentText }],
              model: 'gemini-3.1-flash-lite',
            });
            const outText = genRespText?.text ?? '';
            const jsonStart = outText.indexOf('[');
            const jsonEnd = outText.lastIndexOf(']');
            const jsonStr = jsonStart >= 0 && jsonEnd >= 0 ? outText.slice(jsonStart, jsonEnd + 1) : outText;
            const parsed = JSON.parse(jsonStr);
            function conv(it: any): ChapterRow {
              const name = (it?.name ?? '').toString();
              const aStart = toIntOrNull(it?.start_page ?? it?.start);
              const aEnd = toIntOrNull(it?.end_page ?? it?.end);
              const rawSubs = it?.subchapters ?? it?.sub_chapters ?? it?.children ?? [];
              const subs = Array.isArray(rawSubs) && rawSubs.length > 0 ? rawSubs.map((s: any) => conv(s)) : undefined;
              return { name, printedStart: null, printedEnd: null, alignedStart: aStart, alignedEnd: aEnd, subchapters: subs } as ChapterRow;
            }
            const rows = Array.isArray(parsed) ? parsed.map((it: any) => conv(it)) : [];
            setChapters(rows);
            
          } catch (genErrText: any) {
            
            setError('Failed to run JSON-assisted mapping via Gemini (text mode).');
            setChapters([]);
          }
          setIsProcessing(false);
          return;
        }

        // image mode: load entire PDF (to ensure page bounds) and split only the requested TOC range
        let pdfAll: any = null;
        try {
          pdfAll = await loadPdfDocument(file);
        } catch (e) {
          setError('Failed to load PDF for JSON-assisted mapping.');
          setIsProcessing(false);
          return;
        }
        const totalPages = Number(pdfAll.numPages) || 0;
        if (totalPages <= 0) {
          setError('PDF contains no pages.');
          setIsProcessing(false);
          try { await pdfAll.destroy?.(); } catch {}
          return;
        }

        const parts: { start: number; end: number; name: string }[] = [];
        // Only create parts covering the TOC range (s..e) when in image mode
        const startPage = Math.max(1, Math.min(s, totalPages));
        const endPage = Math.max(startPage, Math.min(e, totalPages));
        for (let p = startPage; p <= endPage; p += per) {
          const start = p;
          const end = Math.min(endPage, p + per - 1);
          parts.push({ start, end, name: `pages_${start}-${end}.pdf` });
        }
        

        const withBlobs: Array<{ start: number; end: number; blob: Blob; name: string }> = [];
        for (const part of parts) {
          
          const blob = await createPdfForRange(file, part.start, part.end);
          withBlobs.push({ start: part.start, end: part.end, blob, name: part.name });
        }

        
        setUploadedFiles(withBlobs.map((b) => ({ name: b.name, state: 'PENDING', blob: b.blob, start: b.start, end: b.end })));

        const entries = withBlobs.map((b) => ({ name: b.name, blob: b.blob, start: b.start, end: b.end }));
        
        const processedFiles = await uploadFilesWithConcurrency(ai, entries, uploadConcurrency);
        

        // Build prompt. Include the JSON text inline so Gemini knows the structure to map.
        let meta = `JSON:${jsonText}\nPARTS:${withBlobs.map((b) => `${b.name}:${b.start}-${b.end}`).join(';')}`;
        let prompt = `You are given a JSON document that lists chapters and optional subchapters for a textbook. The JSON follows nested objects like {name: \\"...\\", subchapters: [{name: \\"...\\"}]}. Using the uploaded PDF parts (which cover the TOC range, named and annotated in the PARTS metadata), determine for each chapter and subchapter the PDF page number(s) where they appear. Return ONLY a JSON array mirroring the input structure but with numeric fields start_page and end_page for each chapter and subchapter. For example: [{\\"name\\":\\"Unit 1\\",\\"start_page\\":12,\\"end_page\\":34,\\"subchapters\\":[{\\"name\\":\\"Section A\\",\\"start_page\\":12,\\"end_page\\":15}]}]. Do not include any extra text outside the JSON. Return ONLY valid JSON. Do not include any introductory text, concluding text, or markdown formatting like \`\`\`json.`;
        // If user requested sending bookmark tree, include OUTLINE_JSON instead of PARTS
        if (sendBookmarkTree) {
          try {
            const outline = await getPdfOutlineTree(pdfAll);
            const outlineJson = JSON.stringify(outline || []);
            meta = `JSON:${jsonText}\nOUTLINE_JSON:${outlineJson.slice(0,250000)}`;
            prompt = `You are given a JSON document that lists chapters and optional subchapters for a textbook and a PDF bookmark (outline) JSON. The bookmark JSON represents nested children using the "items" or "children" fields — treat those as subchapters under their parent chapter. Using the outline JSON, determine for each TOP-LEVEL chapter and for each nested subchapter the PDF page number(s) where they appear. Return ONLY a JSON array mirroring the input structure where each object uses the exact keys: name (string), start_page (integer or null), end_page (integer or null), and subchapters (an array of child objects with the same shape). Always include the "subchapters" key (empty array if none). Preserve nesting depth and do not add extra commentary or text outside the JSON. Return ONLY valid JSON. Do not include any introductory text, concluding text, or markdown formatting like \`\`\`json.`;
          } catch (outlineErr) {
            
            setError('Failed to resolve PDF outline for JSON-assisted mapping.');
            try { await pdfAll.destroy?.(); } catch {}
            setIsProcessing(false);
            return;
          }
        }

        // Build content: prompt + meta + base64 PDF parts
        const contentText = prompt + '\n' + meta;
        const imagesParts: any[] = [];
        for (const wb of withBlobs) {
          const ab = await wb.blob.arrayBuffer();
          const b64 = btoa(new Uint8Array(ab).reduce((d: string, b: number) => d + String.fromCharCode(b), ''));
          imagesParts.push({ mime_type: 'application/pdf', data: b64 });
        }

        let genResp: any = null;
        try {
          
          genResp = await aiBatchGenerate({
            toBeValidated: token,
            messages: [{ role: 'user', content: contentText }],
            model: 'gemini-3.1-flash-lite',
            images: imagesParts.length > 0 ? imagesParts : undefined,
          });
          
        } catch (genErr: any) {
          
          setError(`Failed to contact Gemini API for JSON-assisted mode: ${genErr?.message || String(genErr)}`);
          setIsProcessing(false);
          try { await pdfAll.destroy?.(); } catch {}
          return;
        }

        const out = genResp?.text ?? '';
        try {
          const jsonStart = out.indexOf('[');
          const jsonEnd = out.lastIndexOf(']');
          const jsonStr = jsonStart >= 0 && jsonEnd >= 0 ? out.slice(jsonStart, jsonEnd + 1) : out;
          const parsed = JSON.parse(jsonStr);

          // Convert parsed JSON (assumed to include start_page/end_page and nested subchapters)
          function conv(it: any): ChapterRow {
            const name = (it?.name ?? '').toString();
            const aStart = toIntOrNull(it?.start_page ?? it?.start);
            const aEnd = toIntOrNull(it?.end_page ?? it?.end);
            const rawSubs = it?.subchapters ?? it?.sub_chapters ?? it?.children ?? [];
            const subs = Array.isArray(rawSubs) && rawSubs.length > 0 ? rawSubs.map((s: any) => conv(s)) : undefined;
            return { name, printedStart: null, printedEnd: null, alignedStart: aStart, alignedEnd: aEnd, subchapters: subs } as ChapterRow;
          }

          const rows = Array.isArray(parsed) ? parsed.map((it: any) => conv(it)) : [];
          setChapters(rows);
          
        } catch (parseErr) {
          
          setChapters([]);
        }

        try { await pdfAll.destroy?.(); } catch {}
        setIsProcessing(false);
        return;
      }

      const prompt = `You are given the Table of Contents of a textbook extracted from pages ${s}-${e}. Extract chapter and subchapter entries and output ONLY JSON array with objects like this: [{"name": "Unit-1 health", "start=page": "12", "end-age": "123", "subchapters": [{"name": "1.1 Intro", "start=page": "12", "end-age": "15"}]}]. Use the exact keys: name, start=page, end-age, subchapters. Do not include any extra text outside the JSON. If page ranges are not explicit, infer sensibly or leave fields empty strings. The end page is 1-the next chapter start page. Include subchapters if they are visible in the TOC. Return ONLY valid JSON. Do not include any introductory text, concluding text, or markdown formatting like \`\`\`json.`;

      if (!useImageMode) {
        // 1) Extract text from the specified pages OR send bookmark tree JSON if requested
        
        let response: any = null;
        if (sendBookmarkTree) {
          // Load PDF and resolve outline/bookmark tree
          let pdfDoc: any = null;
          try {
            pdfDoc = await loadPdfDocument(file);
            const outline = await getPdfOutlineTree(pdfDoc);
            try { await pdfDoc.destroy?.(); } catch {}
            const outlineJson = JSON.stringify(outline);
            const outlinePrompt = `You are given a PDF outline (bookmark) tree in JSON format. Using this outline, produce a Table of Contents JSON array with objects like [{"name":"Unit 1","start=page":12,"end-age":34}] where start=page and end-age are PDF page numbers. Return ONLY the JSON array and use the exact keys name, start=page, end-age. Return ONLY valid JSON. Do not include any introductory text, concluding text, or markdown formatting like \`\`\`json.`;
            try {
              
              response = await aiBatchGenerate({
                toBeValidated: token,
                messages: [{ role: 'user', content: `${outlinePrompt}\n\nOUTLINE_JSON:${outlineJson.slice(0,250000)}` }],
                model: 'gemini-3.1-flash-lite',
              });
              
            } catch (genErr: any) {
              
              const msg = (genErr && (genErr.message || String(genErr))) || 'Failed to contact Gemini API';
              setError(`Failed to contact Gemini API: ${msg}.`);
              setIsProcessing(false);
              return;
            }
          } catch (errOutline: any) {
            
            setError('Failed to load PDF outline for bookmark sending.');
            setIsProcessing(false);
            try { await pdfDoc?.destroy?.(); } catch {}
            return;
          }
        } else {
          // default: extract visible text for TOC pages and send to Gemini
          
          const text = await extractPdfTextRange(file, s, e);
          try {
            
            response = await aiBatchGenerate({
              toBeValidated: token,
              messages: [{ role: 'user', content: `${prompt}\n\n${text.slice(0, 250000)}` }],
              model: 'gemini-3.1-flash-lite',
            });
            
          } catch (genErr: any) {
            
            const msg = (genErr && (genErr.message || String(genErr))) || 'Failed to contact Gemini API';
            setError(`Failed to contact Gemini API: ${msg}. Check network/DNS, your API key, or use the server-side proxy if available.`);
            setIsProcessing(false);
            return;
          }
        }
        const out = response?.text ?? '';
        // Try to parse JSON; if fails, log raw
        try {
          const jsonStart = out.indexOf('[');
          const jsonEnd = out.lastIndexOf(']');
          const jsonStr = jsonStart >= 0 && jsonEnd >= 0 ? out.slice(jsonStart, jsonEnd + 1) : out;
          const parsed = JSON.parse(jsonStr);
          const base = parseInt(alignBaseInput, 10);
          const rows = parseChaptersFromOutput(parsed, Number.isFinite(base) && base > 0 ? base : undefined);
          setChapters(rows);
          
        } catch (parseErr) {
          setChapters([]);
          
        }
      } else {
        // Image mode: create PDFs for page ranges then upload and ask Gemini to extract chapters from the files
        const per = Math.max(1, parseInt(pagesPerUpload || '4', 10));
        const parts: { start: number; end: number; blob?: Blob; name?: string }[] = [];
        for (let p = s; p <= e; p += per) {
          const partStart = p;
          const partEnd = Math.min(e, p + per - 1);
          parts.push({ start: partStart, end: partEnd, name: `toc_${partStart}-${partEnd}.pdf` });
        }
        
        // create blobs for each part
        const withBlobs = [] as Array<{ start: number; end: number; blob: Blob; name: string }>;
        for (const part of parts) {
          
          const blob = await createPdfForRange(file, part.start, part.end);
          withBlobs.push({ start: part.start, end: part.end, blob, name: part.name! });
        }

        

        // reset upload status and keep blobs for retries
        setUploadedFiles(withBlobs.map((b) => ({ name: b.name, state: 'PENDING', blob: b.blob, start: b.start, end: b.end })));

        // Convert all blobs to base64 and send via backend
        const imagesParts2: any[] = [];
        for (const wb of withBlobs) {
          setUploadedFiles((prev) => prev.map((f) => (f.name === wb.name ? { ...f, state: 'UPLOADING' } : f)));
          const ab = await wb.blob.arrayBuffer();
          const b64 = btoa(new Uint8Array(ab).reduce((d: string, b: number) => d + String.fromCharCode(b), ''));
          imagesParts2.push({ mime_type: 'application/pdf', data: b64 });
          setUploadedFiles((prev) => prev.map((f) => (f.name === wb.name ? { ...f, state: 'READY' } : f)));
        }

        let response: any = null;
        try {
          
          response = await aiBatchGenerate({
            toBeValidated: token,
            messages: [{ role: 'user', content: prompt }],
            model: 'gemini-3.1-flash-lite',
            images: imagesParts2.length > 0 ? imagesParts2 : undefined,
          });
          
        } catch (genErr: any) {
          
          setError(`Failed to contact Gemini API for file mode: ${genErr?.message || String(genErr)}`);
          setIsProcessing(false);
          return;
        }

        const out = response?.text ?? '';
        try {
          const jsonStart = out.indexOf('[');
          const jsonEnd = out.lastIndexOf(']');
          const jsonStr = jsonStart >= 0 && jsonEnd >= 0 ? out.slice(jsonStart, jsonEnd + 1) : out;
          const parsed = JSON.parse(jsonStr);
          const base = parseInt(alignBaseInput, 10);
          const rows = parseChaptersFromOutput(parsed, Number.isFinite(base) && base > 0 ? base : undefined);
          setChapters(rows);
          
        } catch (parseErr) {
          setChapters([]);
          
        }
      }
    } catch (err: any) {
      
      setError(err?.message || 'Failed to process. Check console for details.');
    } finally {
      setIsProcessing(false);
    }
  };

  // After alignment: chunk chapters by aligned PDF page ranges, with progress
  const handleChunkChapters = async () => {
    try {
      setError(null);
      if (!file) {
        setError('Please choose a PDF file first.');
        return;
      }

      // Load PDF to get total page count and validate aligned ranges
      const pdf = await loadPdfDocument(file);
      const totalPdfPages = Number(pdf.numPages) || 0;

      const alignmentIssues = (chapters || []).map((c, idx) => ({
        idx,
        name: c.name || `Chapter ${idx + 1}`,
        alignedStart: c.alignedStart,
        alignedEnd: c.alignedEnd,
      })).filter(it => it.alignedStart == null || it.alignedEnd == null || it.alignedStart < 1 || (totalPdfPages > 0 && it.alignedEnd > totalPdfPages) || it.alignedEnd < it.alignedStart);

      if (alignmentIssues.length > 0) {
        const list = alignmentIssues.map(i => `${i.name} (start: ${i.alignedStart ?? '-'}, end: ${i.alignedEnd ?? '-'})`).join('; ');
        setError(`Chapter alignment issues detected. Please fix aligned ranges before chunking. Problems: ${list}`);
        try { await pdf.destroy?.(); } catch {}
        return;
      }

      // In university mode, prefer chunking by subchapters when Gemini provided them
      let ranges: Array<any> = [];
      if (institutionMode === 'university') {
        // For each parsed chapter, if it has subchapters use their aligned ranges
        for (let idx = 0; idx < (chapters || []).length; idx++) {
          const c = chapters[idx];
          if (Array.isArray(c.subchapters) && c.subchapters.length > 0) {
            for (let sidx = 0; sidx < c.subchapters.length; sidx++) {
              const sc = c.subchapters[sidx];
              const start = Number(sc.alignedStart || 0);
              const end = Number(sc.alignedEnd || 0);
              if (Number.isFinite(start) && Number.isFinite(end) && start > 0 && end >= start) {
                ranges.push({ parentIdx: idx, subIdx: sidx, name: `${c.name} / ${sc.name}`, start, end, parentName: c.name, subName: sc.name });
              }
            }
          } else {
            // fallback to whole chapter if no subchapters
            const start = Number(c.alignedStart || 0);
            const end = Number(c.alignedEnd || 0);
            if (Number.isFinite(start) && Number.isFinite(end) && start > 0 && end >= start) {
              ranges.push({ parentIdx: idx, subIdx: null, name: c.name, start, end, parentName: c.name, subName: null });
            }
          }
        }
      } else {
        ranges = (chapters || []).map((c, idx) => ({
          idx,
          name: c.name || `Chapter ${idx + 1}`,
          start: Number(c.alignedStart || 0),
          end: Number(c.alignedEnd || 0),
        })).filter(r => Number.isFinite(r.start) && Number.isFinite(r.end) && r.start > 0 && r.end >= r.start);
      }

      if (ranges.length === 0) {
        setError('No chapters have valid aligned page ranges. Please set alignment first.');
        try { await pdf.destroy?.(); } catch {}
        return;
      }

      const totalPages = ranges.reduce((acc, r) => acc + (r.end - r.start + 1), 0);
      setChunkSummaries([]);
      setChunkItems([]);
      setIsChunking(true);
      setChunkProgress({ completed: 0, total: totalPages, label: 'Preparing…' });

      let done = 0;
      const summaries: { name: string; start: number; end: number; chunks: number }[] = [];
      const items: ChunkItem[] = [];

      for (let ri = 0; ri < ranges.length; ri++) {
        const r = ranges[ri];
        let chapterCount = 0;
        for (let p = r.start; p <= r.end; p++) {
          const text = await extractPageText(pdf, p);
          const norm = (text || '').replace(/\0/g, '').trim();
          if (norm.length > 0) {
            // For university mode we keep parent/sub indices on the chunk item for later saving
            const chunk: ChunkItem = {
              chapterIndex: ri,
              chapterName: r.name,
              page: p,
              text: norm,
              parentChapterIndex: r.parentIdx ?? r.idx ?? null,
              subchapterIndex: (typeof r.subIdx !== 'undefined') ? r.subIdx : null,
              parentChapterTitle: r.parentName ?? r.name,
              subchapterTitle: r.subName ?? null,
            };
            items.push(chunk);
            chapterCount += 1;
          }
          done += 1;
          setChunkProgress({ completed: done, total: totalPages, label: `${r.name}: page ${p} of ${r.end}` });
        }
        summaries.push({ name: r.name, start: r.start, end: r.end, chunks: chapterCount });
      }

      try { await pdf.destroy?.(); } catch {}
      setChunkSummaries(summaries);
      setChunkItems(items);
    } catch (e: any) {
      
      setError(e?.message || 'Failed to chunk chapters.');
    } finally {
      setIsChunking(false);
    }
  };

  const EMBEDDING_MODEL = 'gemini-embedding-001';

  const handleEmbedChunks = async () => {
    try {
      setError(null);
      if (!apiKey.trim()) {
        
        setError('Please enter your Gemini API key.');
        return;
      }
      if (!chunkItems || chunkItems.length === 0) {
        
        setError('No chunks to embed. Please run chunking first.');
        return;
      }
      setIsEmbedding(true);
      setEmbedStartedAt(Date.now());
      setEmbedSummaries([]);
      setEmbedProgress({ completed: 0, total: chunkItems.length, label: 'Preparing…' });

      // Helper: split array into groups of 10
      const groups: ChunkItem[][] = [];
      for (let i = 0; i < chunkItems.length; i += 10) {
        groups.push(chunkItems.slice(i, i + 10));
      }
      

  let completed = 0;
  const perChapter = new Map<string, number>();
  const details = new Map<string, Array<{ page: number; text: string; raw: any }>>();

      for (let gi = 0; gi < groups.length; gi++) {
        const group = groups[gi];
        try {
          // Embed via backend proxy
          setEmbedProgress({ completed, total: chunkItems.length, label: `Embedding group ${gi + 1}/${groups.length}…` });
          const contentsArr = group.map((ci) => ci.text);
          const resp: any = await aiEmbedContent({
            toBeValidated: token,
            contents: contentsArr,
            model: EMBEDDING_MODEL,
          });

          // If we get a successful response, count the items (best-effort)
          const responsesArr = resp?.embeddings ?? resp?.responses ?? resp?.data ?? resp?.items ?? resp?.outputs ?? [];
          const produced = Array.isArray(responsesArr) ? responsesArr.length : group.length;
          for (let i = 0; i < group.length; i++) {
            const ci = group[i];
            perChapter.set(ci.chapterName, (perChapter.get(ci.chapterName) || 0) + 1);
            const rawItem = responsesArr[i] ?? null;
            const rawPreview = normalizeEmbeddingItem(rawItem);
            const arr = details.get(ci.chapterName) ?? [];
            arr.push({ page: ci.page, text: ci.text, raw: rawPreview });
            details.set(ci.chapterName, arr);
          }
          completed += produced;
          setEmbedProgress({ completed, total: chunkItems.length, label: `Completed group ${gi + 1}/${groups.length}` });
          // persist interim details to state so UI can preview during long runs
          setEmbedDetails(Object.fromEntries(Array.from(details.entries())));
          
        } catch (err: any) {
          
          setEmbedProgress({ completed, total: chunkItems.length, label: `Group ${gi + 1} failed; falling back small groups…` });

          // Fallback: split group into small batches (2-5) and call embedContent for each small group
          const FALLBACK_BATCH_SIZE = 4;
          const smallGroups: ChunkItem[][] = [];
          for (let si = 0; si < group.length; si += FALLBACK_BATCH_SIZE) {
            smallGroups.push(group.slice(si, si + FALLBACK_BATCH_SIZE));
          }

          for (let sgi = 0; sgi < smallGroups.length; sgi++) {
            const smallGroup = smallGroups[sgi];
            try {
              const smallContents = smallGroup.map((ci) => ci.text);
              const sresp: any = await aiEmbedContent({
                toBeValidated: token,
                contents: smallContents,
                model: EMBEDDING_MODEL,
              });
              const sResponsesArr = sresp?.embeddings ?? sresp?.responses ?? sresp?.data ?? sresp?.items ?? sresp?.outputs ?? [];
              const count = Array.isArray(sResponsesArr) ? sResponsesArr.length : smallGroup.length;
              for (let i = 0; i < smallGroup.length; i++) {
                const ci = smallGroup[i];
                perChapter.set(ci.chapterName, (perChapter.get(ci.chapterName) || 0) + 1);
                const rawItem = sResponsesArr[i] ?? null;
                const rawPreview = normalizeEmbeddingItem(rawItem);
                const arr = details.get(ci.chapterName) ?? [];
                arr.push({ page: ci.page, text: ci.text, raw: rawPreview });
                details.set(ci.chapterName, arr);
              }
              completed += count;
              // persist interim details after each small batch
              setEmbedDetails(Object.fromEntries(Array.from(details.entries())));
              
            } catch (se) {
              
            }
            setEmbedProgress({ completed, total: chunkItems.length, label: `Fallback processed ${sgi + 1}/${smallGroups.length}` });
          }
        }
      }

      // Finalize summary
      const summaryArr = Array.from(perChapter.entries()).map(([chapterName, count]) => ({ chapterName, count }));
      setEmbedSummaries(summaryArr);
      setEmbedProgress({ completed, total: chunkItems.length, label: 'Embedding completed' });
      // mark a flag so UI shows Finish button
      setTimeout(() => {
        // delay a tick to allow UI to update
      }, 0);
    } catch (e: any) {
      
      setError(e?.message || 'Failed to embed chunks.');
    } finally {
      setIsEmbedding(false);
    }
  };

  // Handlers for manual alignment editing
  const handleChapterChange = (index: number, field: 'alignedStart' | 'alignedEnd', value: string) => {
    const val = parseInt(value, 10);
    const num = Number.isFinite(val) ? val : null;
    setChapters(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: num };
      return next;
    });
  };

  const handleSubchapterChange = (chIdx: number, subIdx: number, field: 'alignedStart' | 'alignedEnd', value: string) => {
    const val = parseInt(value, 10);
    const num = Number.isFinite(val) ? val : null;
    setChapters(prev => {
      const next = [...prev];
      const ch = { ...next[chIdx] };
      if (ch.subchapters) {
        const subs = [...ch.subchapters];
        subs[subIdx] = { ...subs[subIdx], [field]: num };
        ch.subchapters = subs;
      }
      next[chIdx] = ch;
      return next;
    });
  };

  const handleDeleteChapter = (index: number) => {
    if (confirm('Are you sure you want to delete this chapter?')) {
      setChapters(prev => prev.filter((_, i) => i !== index));
    }
  };

  // Save book and chunks to backend (Finish)
  const handleSaveBook = async () => {
    if (!bookName || !chunkItems || chunkItems.length === 0) {
      setError('No book or chunks to save');
      return;
    }
    try {
      const payload: any = { title: bookName, author: author.trim(), chapters: [] };
      // Build chapters array from chunkItems grouping by chapter/subchapter when in university mode
      const byGroup: Record<string, any[]> = {};
      const groupMeta: Record<string, any> = {};
      for (const ci of chunkItems) {
        const embDetails = embedDetails?.[ci.chapterName] ?? [];
        // try to find embedding for this page/text
        const found = embDetails.find((d:any) => d.page === ci.page && d.text && d.text.startsWith((ci.text || '').slice(0,50)));
        const embedding = found?.raw?.embedding_vector ?? found?.raw?.sample ?? null;
  // Determine grouping key: for university use parentIdx_subIdx, otherwise use chapterIndex
  const parentIdx = (ci as any).parentChapterIndex;
  const subIdx = (ci as any).subchapterIndex;
  const groupKey = (institutionMode === 'university' && parentIdx != null) ? `p${parentIdx}_s${subIdx ?? 'null'}` : `c${ci.chapterIndex}`;
  byGroup[groupKey] = byGroup[groupKey] || [];

        // Compute the original printed page (page_no) to persist.
        // Prefer per-(sub)chapter mapping when available: printed = printedStart + (alignedPage - alignedStart)
        // Otherwise fallback to global alignment base (alignBaseInput): printed = alignedPage - base + 1
        // If neither is available, save the aligned PDF page as a fallback.
        let printedPage: number | null = null;
        try {
          const alignedPage = Number(ci.page);
          // If university mode and we have parent/sub indices, prefer their mapping
          if (institutionMode === 'university' && parentIdx != null) {
            const parent = (chapters || [])[parentIdx] as any;
            const sub = (parent?.subchapters && typeof subIdx === 'number') ? parent.subchapters[subIdx] : null;
            const alignedStart = sub?.alignedStart ?? parent?.alignedStart;
            const printedStart = sub?.printedStart ?? parent?.printedStart;
            if (alignedStart != null && printedStart != null) {
              printedPage = Number(printedStart) + (alignedPage - Number(alignedStart));
            }
          }
          if (printedPage == null) {
            // fallback to chapter-level mapping
            if (ci.chapterIndex != null) {
              const ch = (chapters || [])[ci.chapterIndex] as any;
              if (ch && Number.isFinite(ch?.alignedStart) && Number.isFinite(ch?.printedStart)) {
                printedPage = Number(ch.printedStart) + (alignedPage - Number(ch.alignedStart));
              }
            }
          }
          if (printedPage == null) {
            const baseNum = parseInt(alignBaseInput || '', 10);
            if (Number.isFinite(baseNum) && baseNum > 0) {
              printedPage = alignedPage - baseNum + 1;
            } else {
              printedPage = alignedPage;
            }
          }
        } catch (e) {
          printedPage = ci.page;
        }
        if (!Number.isFinite(printedPage as number) || printedPage == null) printedPage = ci.page;
        if ((printedPage as number) < 1) printedPage = 1;

        byGroup[groupKey].push({ chunk_id: `${groupKey}-${ci.page}`, text: ci.text, embedding, page_no: printedPage });

        // record meta for this group (title, subchapter_title, start/end pages, api mapping)
        if (!groupMeta[groupKey]) {
          if (institutionMode === 'university' && parentIdx != null) {
            const parent = (chapters || [])[parentIdx] as any;
            const sub = (parent?.subchapters && typeof subIdx === 'number') ? parent.subchapters[subIdx] : null;
            const start = sub ? sub.alignedStart : parent?.alignedStart;
            const end = sub ? sub.alignedEnd : parent?.alignedEnd;
            const apiId = sub ? (subchapterMappings?.[`${parentIdx}_${subIdx}`] ?? null) : (chapterMappings[parentIdx] ?? null);
            groupMeta[groupKey] = { number: (parentIdx + 1), title: parent?.name ?? '', subchapter_title: sub?.name ?? null, start_page: start ?? null, end_page: end ?? null, api_chapter_id: apiId };
          } else {
            // school flow or fallback: group by chapterIndex
            const chIdx = ci.chapterIndex;
            const ch = (chapters || [])[chIdx] as any;
            groupMeta[groupKey] = { number: (chIdx + 1), title: ch?.name ?? '', subchapter_title: null, start_page: ch?.alignedStart ?? null, end_page: ch?.alignedEnd ?? null, api_chapter_id: chapterMappings[chIdx] ?? null };
          }
        }
      }

      // correlate groups with chapters state and build payload entries
      for (const [gk, chunks] of Object.entries(byGroup)) {
        const meta = groupMeta[gk] || {};
        payload.chapters.push({ number: meta.number || 0, title: meta.title || '', subchapter_title: meta.subchapter_title || null, start_page: meta.start_page || null, end_page: meta.end_page || null, api_chapter_id: meta.api_chapter_id || null, chunks });
      }

      // Add metadata linking: course (university) or grade/subject (school)
      if (institutionMode === 'university') {
        // prefer selectedCourse
        payload.course_id = selectedCourse;
      } else {
        payload.school_grade_id = selectedGrade;
        payload.school_subject_id = selectedSubject;
      }

      const data = await request('/api/textbook/save_book/', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify(payload) });
      
      
      // Saved: back to the library, where the new book is listed.
      setError(null);
      navigate('/admin/rag');
    } catch (e:any) {
      
      setError(e?.message || 'Save failed');
    }
  };


  if (role !== 'superuser') {
    return (
      <div className="rounded-[12px] border border-danger bg-danger-bg p-4 text-danger-ink">
        403 - Not authorized
      </div>
    );
  }

  // Display-only values for the step notes (derived from the wizard state above).
  const percentOf = (p: { completed: number; total: number }) => (p.total > 0 ? (p.completed / p.total) * 100 : 0);
  const subchapterTotal = (chapters || []).reduce((n, c) => n + (c.subchapters?.length ?? 0), 0);
  const mappedCount = (chapters || []).filter((_, i) => chapterMappings[i] != null).length;
  const chunkTotal = chunkItems?.length ?? 0;
  const maxChapterChunks = Math.max(1, ...(chunkSummaries || []).map((s) => s.chunks));
  const embeddedTotal = embedSummaries.reduce((n, s) => n + s.count, 0);
  const TH = 'whitespace-nowrap px-2.5 pb-2.5 text-left font-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-muted';
  // Design `.cin`: 72 x 36, mono 13px.
  const CELL_INPUT = 'block min-h-[36px] w-[72px] rounded-[10px] border bg-surface px-2.5 font-mono text-[13px] font-medium text-ink outline-none focus:border-accent focus-visible:ring-2 focus-visible:ring-accent';
  // Design `.lb`: 13px / 600 / ink field label.
  const LBL = 'block text-[13px] font-semibold text-ink';
  const gradeLabel = (grades.find((g) => g.id === selectedGrade)?.grade_name ?? grades.find((g) => g.id === selectedGrade)?.name) as string | undefined;
  const subjectLabel = (subjects.find((x) => x.id === selectedSubject)?.subject_name ?? subjects.find((x) => x.id === selectedSubject)?.name) as string | undefined;
  const matchScope = institutionMode === 'school'
    ? [gradeLabel, subjectLabel].filter(Boolean).join(' ')
    : courses.find((c) => c.id === selectedCourse)?.name ?? '';
  const embedRemainingMin = (() => {
    if (!embedStartedAt || embedProgress.completed <= 0 || embedProgress.total <= embedProgress.completed) return null;
    const perChunkMs = (Date.now() - embedStartedAt) / embedProgress.completed;
    return Math.max(1, Math.ceil((perChunkMs * (embedProgress.total - embedProgress.completed)) / 60000));
  })();
  // Per-chapter embedding state: Done once every chunk of the chapter has an embedding, the first unfinished one is Embedding.
  const embedRows = (() => {
    const totals = new Map<string, number>();
    for (const ci of chunkItems || []) totals.set(ci.chapterName, (totals.get(ci.chapterName) ?? 0) + 1);
    let current = true;
    return Array.from(totals.entries()).map(([name, total]) => {
      const done = (embedDetails?.[name]?.length ?? 0) >= total;
      let state: 'Done' | 'Embedding' | 'Queued' = 'Queued';
      if (done) state = 'Done';
      else if (isEmbedding && current) { state = 'Embedding'; current = false; }
      return { name, state };
    });
  })();
  const OPTION_CARD = 'rounded-[14px] border border-line bg-surface p-4';
  const errorBox = error ? (
    <div role="alert" className="mt-[18px] rounded-[12px] border border-danger bg-danger-bg px-3.5 py-2.5 text-sm text-danger-ink">{error}</div>
  ) : null;
  const nextLabel = (text: string) => <>{text} <span aria-hidden>→</span></>;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="RAG knowledge · New source"
        title="Set up a new source"
        subtitle="Upload a textbook, map it to the curriculum, chunk it and index it for AI notes and chat."
        actions={(
          <Link to="/admin/rag"
            onClick={(e) => {
              // Leaving mid-wizard throws the work away, so ask first.
              if (file || currentStep > 1) { e.preventDefault(); setConfirmLeave(true); }
            }}
            className={LINK_BTN}>
            <ArrowLeft size={16} aria-hidden /><span>Back to library</span>
          </Link>
        )}
      />

      <div className="grid grid-cols-1 items-start gap-[18px] min-[1181px]:grid-cols-12">
        <RagSetupProgress step={currentStep} complete={!isEmbedding && embedSummaries.length > 0} busy={isProcessing || isChunking || isEmbedding} onGoToStep={(n) => setCurrentStep(n)} className="min-[1181px]:col-span-3" />

        <section className="min-w-0 rounded-[20px] border border-line bg-surface p-[22px] shadow-card min-[1181px]:col-span-9">
          <RagStepHeader
            step={currentStep}
            aside={currentStep === 3 ? (
              <label className="inline-flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" className={RAG_CHECK} checked={showStep3Preview} onChange={(e) => setShowStep3Preview(e.target.checked)} />
                Show PDF preview at bottom
              </label>
            ) : undefined}
          />

          {currentStep === 1 && (
            <>
              <div className="flex flex-col gap-[18px]">
                <div className="flex flex-wrap items-center gap-4 rounded-[16px] border-2 border-dashed border-cell-dash bg-surface-2 p-[22px]">
                  <span aria-hidden className="grid h-11 w-11 flex-none place-items-center rounded-[14px] bg-navy text-white"><FileText size={20} /></span>
                  <div className="min-w-[200px] flex-1">
                    {file ? (
                      <p className="font-semibold text-ink">{file.name}</p>
                    ) : (
                      <label htmlFor="pdfInput" className="block font-semibold text-ink">Select PDF file</label>
                    )}
                    <p className="text-[13px] text-muted">{fileInfo ?? 'Choose the textbook PDF.'}</p>
                    <input
                      id="pdfInput"
                      aria-label="Select PDF file"
                      type="file"
                      accept="application/pdf"
                      onChange={handleFileChange}
                      className="sr-only"
                    />
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={() => document.getElementById('pdfInput')?.click()}>{file ? 'Replace PDF' : 'Choose PDF'}</Button>
                  {fileInfo && (
                    <Button type="button" variant="ghost" size="sm" onClick={clearSelection}>Clear</Button>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                  <FormField label="Book name" htmlFor="bookName">
                    <input id="bookName" className={RAG_INPUT} value={bookName} onChange={(e) => setBookName(e.target.value)} placeholder="e.g., Biology Grade 7" />
                  </FormField>
                  <FormField label="Author / publisher" htmlFor="bookAuthor">
                    <input id="bookAuthor" className={RAG_INPUT} value={author} onChange={(e) => setAuthor(e.target.value)} placeholder="e.g., Ministry of Education" />
                  </FormField>
                </div>

                <div className="space-y-1">
                  <span className={LBL}>Institution mode</span>
                  <Segmented<'school' | 'university'>
                    label="Institution mode"
                    value={institutionMode}
                    onChange={setInstitutionMode}
                    options={[{ key: 'school', label: 'School' }, { key: 'university', label: 'University / College' }]}
                  />
                </div>

                {institutionMode === 'school' && (
                  <div className="space-y-3">
                    <div className="min-w-0 space-y-1">
                      <span className={LBL} aria-hidden="true">Grade</span>
                      <PillTabs scroll dense label="Grade" value={selectedGrade == null ? '' : String(selectedGrade)}
                        onChange={(k) => { const v = k ? parseInt(k, 10) : null; setSelectedGrade(v); setSelectedSubject(null); }}
                        options={grades.map((g) => ({ key: String(g.id), label: String(g.grade_name ?? g.name ?? g.id) }))} />
                    </div>
                    {selectedGrade != null && (
                      <div className="min-w-0 space-y-1">
                        <span className={LBL} aria-hidden="true">Subject</span>
                        <PillTabs scroll dense label="Subject" value={selectedSubject == null ? '' : String(selectedSubject)}
                          onChange={(k) => setSelectedSubject(k ? parseInt(k, 10) : null)}
                          options={subjects.map((x) => ({ key: String(x.id), label: String(x.subject_name ?? x.name ?? x.id) }))} />
                      </div>
                    )}
                  </div>
                )}

                {institutionMode === 'university' && (
                  <div className="space-y-3">
                    <div className="min-w-0 space-y-1">
                      <span className={LBL} aria-hidden="true">Field of Study</span>
                      {fieldsLoading ? (
                        <p className="text-xs text-muted">Loading fields...</p>
                      ) : fields.length === 0 ? (
                        <p className="text-xs text-muted">No fields available</p>
                      ) : (
                        <PillTabs scroll dense label="Field of Study" value={selectedField == null ? '' : String(selectedField)}
                          onChange={(k) => setSelectedField(k ? parseInt(k, 10) : null)}
                          options={fields.map((f) => ({ key: String(f.id), label: f.name }))} />
                      )}
                    </div>
                    {selectedField != null && (
                      <div className="min-w-0 space-y-1">
                        <span className={LBL} aria-hidden="true">Academic Year</span>
                        <PillTabs scroll dense label="Academic Year" value={selectedAcademicYear == null ? '' : String(selectedAcademicYear)}
                          onChange={(k) => setSelectedAcademicYear(k ? parseInt(k, 10) : null)}
                          options={academicYears.map((y) => ({ key: String(y.id), label: y.name }))} />
                      </div>
                    )}
                    {selectedAcademicYear != null && (
                      courses.length > 30 ? (
                        <FormField label="Course" htmlFor="courseSelect">
                          <select id="courseSelect" aria-label="Course" title="Course" className={RAG_INPUT} value={selectedCourse ?? ''} onChange={(e) => setSelectedCourse(e.target.value ? parseInt(e.target.value,10) : null)}>
                            <option value="">-- choose course --</option>
                            {courses.map(c => (<option key={c.id} value={c.id}>{c.name}</option>))}
                          </select>
                        </FormField>
                      ) : (
                        <div className="min-w-0 space-y-1">
                          <span className={LBL} aria-hidden="true">Course</span>
                          <PillTabs scroll dense label="Course" value={selectedCourse == null ? '' : String(selectedCourse)}
                            onChange={(k) => setSelectedCourse(k ? parseInt(k, 10) : null)}
                            options={courses.map((c) => ({ key: String(c.id), label: c.name }))} />
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>

              <RagDevTools>
                <p>Chapters JSON</p>
                <pre className="max-h-64 overflow-auto rounded-[10px] bg-surface p-3 text-[12px] text-ink">{JSON.stringify(chapters, null, 2)}</pre>
              </RagDevTools>

              {errorBox}

              <RagStepFooter note={file ? (filePages ? `${filePages} pages ready` : 'PDF ready to preview') : 'No PDF chosen yet'}>
                <Button type="button" onClick={() => setCurrentStep(2)} disabled={!file || !bookName.trim() || (institutionMode === 'school' ? (!selectedGrade || !selectedSubject) : (!selectedField || !selectedAcademicYear || !selectedCourse))}>{nextLabel('Continue')}</Button>
              </RagStepFooter>
            </>
          )}

          {currentStep === 2 && (
            <>
              <div className="grid grid-cols-1 gap-[18px] min-[1181px]:grid-cols-12">
                <div className="min-w-0 min-[1181px]:col-span-5">
                  <p className={`mb-2.5 ${RAG_SECTION_LABEL}`}>Preview{thumbs.length > 0 ? ` · pages 1–${thumbs.length}` : ''}</p>
                  {!previewUrl ? (
                    <div className="rounded-[14px] border border-dashed border-line-strong p-6 text-center text-sm text-muted">
                      No file selected. Go back and choose a PDF.
                    </div>
                  ) : (
                    <>
                      {thumbs.length > 0 && (
                        <div className="grid grid-cols-3 gap-2.5">
                          {thumbs.map((src, i) => (
                            <div key={i} aria-hidden="true" className="relative aspect-[3/4] overflow-hidden rounded-[10px] bg-mist">
                              <img src={src} alt="" className="h-full w-full object-cover" />
                              <b className="absolute bottom-2 right-2.5 rounded bg-surface/80 px-1 font-mono text-[11px] font-semibold text-muted">{i + 1}</b>
                            </div>
                          ))}
                        </div>
                      )}
                      <Button type="button" variant="ghost" size="sm" className="mt-3" aria-expanded={showFullPreview} onClick={() => setShowFullPreview((v) => !v)}>
                        {showFullPreview ? 'Hide full PDF' : 'Open full PDF'}
                      </Button>
                      {showFullPreview && (
                        <div className="mt-3 overflow-hidden rounded-[14px] border border-line">
                          <iframe title="PDF Preview" src={previewUrl} className="h-[60vh] w-full" />
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="flex min-w-0 flex-col gap-2.5 min-[1181px]:col-span-7">
                  <p className={RAG_SECTION_LABEL}>Table of contents</p>

                  <RadioCards<'gemini' | 'bookmarks' | 'json'>
                    label="Table of contents method"
                    value={tocMethod}
                    onChange={(m) => { setTocMethod(m); setSendBookmarkTree(m === 'bookmarks'); }}
                    options={[
                      { key: 'gemini', label: 'Extract with Gemini (recommended)', hint: 'Reads the contents pages and finds chapter and subchapter page numbers.' },
                      { key: 'bookmarks', label: 'Use PDF bookmarks', hint: 'Sends the PDF bookmark tree to Gemini instead of the contents pages.' },
                      { key: 'json', label: 'Upload TOC JSON', hint: 'Skip extraction with a prepared chapters file.' },
                    ]}
                  />

                  {tocMethod !== 'json' && (
                  <div className={OPTION_CARD}>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <FormField label="TOC Start Page" htmlFor="tocStart">
                        <input id="tocStart" className={RAG_INPUT} type="number" min={1} value={tocStart} onChange={(e) => setTocStart(e.target.value)} placeholder="e.g., 1" />
                      </FormField>
                      <FormField label="TOC End Page" htmlFor="tocEnd">
                        <input id="tocEnd" className={RAG_INPUT} type="number" min={1} value={tocEnd} onChange={(e) => setTocEnd(e.target.value)} placeholder="e.g., 5" />
                      </FormField>
                    </div>
                    <div className="mt-3 flex flex-col gap-2 text-sm text-ink">
                      <label className="inline-flex items-start gap-2">
                        <input type="checkbox" className={`mt-0.5 ${RAG_CHECK}`} checked={useImageMode} onChange={(e) => setUseImageMode(e.target.checked)} />
                        Use image/file upload mode for TOC extraction (send pages as PDF files)
                      </label>
                      {useImageMode && (
                        <FormField label="Pages per upload (PDF)" htmlFor="pagesPerUpload">
                          <input id="pagesPerUpload" className={RAG_INPUT} type="number" min={1} value={pagesPerUpload} onChange={(e) => setPagesPerUpload(e.target.value)} placeholder="e.g., 4" />
                        </FormField>
                      )}
                      {institutionMode === 'university' && (
                        <>
                          <label className="inline-flex items-start gap-2">
                            <input type="checkbox" className={`mt-0.5 ${RAG_CHECK}`} checked={useJsonMode} onChange={(e) => setUseJsonMode(e.target.checked)} />
                            Use JSON-assisted mapping (upload JSON of chapters/subchapters)
                          </label>
                          {useJsonMode && (
                            <FormField label="Upload chapters JSON" htmlFor="jsonInput" hint="When enabled, the uploaded JSON will be sent to Gemini along with PDF page chunks to determine page numbers for chapters and subchapters.">
                              <input id="jsonInput" aria-label="Upload chapters JSON file" type="file" accept="application/json,.json" onChange={(e) => { const f = e.target.files?.[0] || null; setJsonFile(f); }} className={RAG_FILE_INPUT} />
                            </FormField>
                          )}
                        </>
                      )}
                      <label className="inline-flex items-start gap-2 text-muted">
                        <input type="checkbox" className={`mt-0.5 ${RAG_CHECK}`} checked={useStaging} onChange={(e) => setUseStaging(e.target.checked)} />
                        Use Gemini sandbox (autopush) endpoint
                      </label>
                    </div>
                  </div>
                  )}

                  {tocMethod === 'json' && (
                  <div className={OPTION_CARD}>
                    <div className="flex flex-col gap-3">
                      <input
                        id="directJsonInput"
                        aria-label="Upload pre-formatted TOC JSON"
                        type="file"
                        accept="application/json"
                        onChange={(e) => setDirectJsonFile(e.target.files?.[0] || null)}
                        className={RAG_FILE_INPUT}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleDirectJsonUpload}
                        disabled={!directJsonFile || isProcessing}
                        loading={isProcessing}
                        className="w-fit"
                      >
                        Upload & Use Directly
                      </Button>
                    </div>
                  </div>
                  )}

                  <Callout icon={apiKey?.trim() ? <Check size={16} className="text-success" /> : <KeyRound size={16} />}>
                    {apiKey?.trim() ? 'Gemini API key saved for your account.' : 'No Gemini API key yet.'}{' '}
                    <button type="button" onClick={() => setShowApiKeyModal(true)} className="font-semibold text-ink underline underline-offset-2 hover:text-muted">{apiKey?.trim() ? 'Manage key' : 'Add key'}</button>
                  </Callout>
                </div>
              </div>

              <RagDevTools>
                <Button type="button" variant="ghost" size="sm" onClick={async () => { await logBookmarkTree(); }}>Log bookmark tree</Button>
              </RagDevTools>

              {errorBox}

              <RagStepFooter onBack={() => setCurrentStep(1)} note={tocMethod === 'json' ? 'Upload a TOC file to continue' : tocStart && tocEnd ? `Contents on PDF pages ${tocStart}–${tocEnd}` : 'Enter the contents pages or upload a TOC file'}>
                {tocMethod !== 'json' && (<Button
                  type="button"
                  onClick={async () => {
                    const ok = await (async () => {
                      try {
                        if (!file) { setError('Please choose a PDF file first.'); return false; }
                        if (!tocStart || !tocEnd) { setError('Please enter TOC start and end pages.'); return false; }
                        await handleContinue();
                        return true;
                      } catch {
                        return false;
                      }
                    })();
                    if (ok) setCurrentStep(3);
                  }}
                  loading={isProcessing}
                  disabled={isProcessing || !file || !tocStart || !tocEnd}
                >
                  {nextLabel('Extract chapters')}
                </Button>)}
              </RagStepFooter>
            </>
          )}

          {currentStep === 3 && (
            <>
              <p className="mb-[18px] text-sm text-muted">Sometimes PDF page indices differ from printed page numbers. Enter the PDF page index where printed page 1 appears; we will align each chapter's printed start/end to real PDF pages. If the final chapter has no end, applying alignment sets it to the PDF's last page.</p>

              <div className="mb-[18px] grid grid-cols-1 gap-[18px] md:grid-cols-2">
                <div className="flex items-end gap-2.5">
                  <div className="min-w-0 flex-1">
                    <FormField label="PDF page where printed page 1 exists" htmlFor="alignBase">
                      <input id="alignBase" className={RAG_INPUT} type="number" min={1} value={alignBaseInput} onChange={(e) => setAlignBaseInput(e.target.value)} placeholder="e.g., 7" />
                    </FormField>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={async () => {
                      const b = parseInt(alignBaseInput, 10);
                      if (Number.isFinite(b) && b > 0) {
                        try {
                          await applyAlignment(b);
                          // show transient notice so user sees alignment applied without opening console
                          setAlignmentNotice('Alignment applied');
                          setTimeout(() => setAlignmentNotice(null), 3000);
                        } catch (e: any) {

                          setError(e?.message || 'Failed to apply alignment.');
                        }
                      } else setError('Please enter a valid PDF page number for alignment (>= 1).');
                    }}
                  >
                    Apply Alignment
                  </Button>
                </div>
                <div className="flex items-end gap-2.5">
                  <div className="min-w-0 flex-1">
                    <FormField label="Set last chapter end (PDF page)" htmlFor="lastChapterEnd">
                      <input id="lastChapterEnd" className={RAG_INPUT} type="number" min={1} value={lastChapterEndInput} onChange={(e) => setLastChapterEndInput(e.target.value)} placeholder="e.g., 400" />
                    </FormField>
                  </div>
                  <Button type="button" variant="ghost" onClick={() => setLastChapterEnd()}>Set Last End</Button>
                </div>
              </div>

              {alignmentNotice && (
                <div role="status" className="mb-[18px] rounded-[12px] bg-success-bg px-3.5 py-2.5 text-sm text-success">{alignmentNotice}</div>
              )}

              {(!chapters || chapters.length === 0) ? (
                <div className="rounded-[14px] border border-dashed border-line-strong p-4 text-center text-sm text-muted">
                  No chapters parsed yet. You can go back to adjust TOC pages and regenerate.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[640px] text-sm">
                    <thead>
                      <tr>
                        <th className={TH}>#</th>
                        <th className={TH}>Chapter title</th>
                        <th className={`${TH} text-right`}>Start page</th>
                        <th className={`${TH} text-right`}>End page</th>
                        <th className={`${TH} text-right`}>Subchapters</th>
                      </tr>
                    </thead>
                    {rangePg.pageItems.map((c, i0) => {
                      const idx = rangePg.from - 1 + i0;
                      return (
                      <tbody key={`${idx}-${forceRerender}`} className="border-t border-hairline">
                        <tr>
                          <td className="px-2.5 py-3 align-top font-mono text-muted">{idx + 1}</td>
                          <td className="px-2.5 py-3 align-top">
                            <div className="font-semibold text-ink">{c.name || `Chapter ${idx + 1}`}</div>
                            <div className="mt-0.5 text-xs text-muted">Printed: {c.printedStart ?? '-'}{(c.printedStart!=null||c.printedEnd!=null)?' - ':''}{c.printedEnd ?? ''}</div>
                          </td>
                          <td className="px-2.5 py-3 align-top">
                            <label htmlFor={`alignedStart-${idx}`} className="sr-only">Start (PDF)</label>
                            <input
                              id={`alignedStart-${idx}`}
                              type="number"
                              value={c.alignedStart != null ? String(c.alignedStart) : ''}
                              onChange={(e) => handleChapterChange(idx, 'alignedStart', e.target.value)}
                              className={`ml-auto ${CELL_INPUT} ${(!c.alignedStart || (c.alignedEnd && c.alignedStart > c.alignedEnd)) ? 'border-danger' : 'border-line-strong'}`}
                            />
                          </td>
                          <td className="px-2.5 py-3 align-top">
                            <label htmlFor={`alignedEnd-${idx}`} className="sr-only">End (PDF)</label>
                            <input
                              id={`alignedEnd-${idx}`}
                              type="number"
                              value={c.alignedEnd != null ? String(c.alignedEnd) : ''}
                              onChange={(e) => handleChapterChange(idx, 'alignedEnd', e.target.value)}
                              className={`ml-auto ${CELL_INPUT} ${(!c.alignedEnd) ? 'border-danger' : 'border-line-strong'}`}
                            />
                          </td>
                          <td className="px-2.5 py-3 text-right align-top font-mono text-ink">{c.subchapters?.length ?? 0}</td>
                          <td className="px-2.5 py-3 text-right align-top">
                            <button type="button" onClick={() => handleDeleteChapter(idx)} className="grid h-9 w-9 place-items-center rounded-[10px] text-danger hover:bg-danger-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                              <span className="sr-only">Delete</span>
                              <Trash2 size={16} aria-hidden />
                            </button>
                          </td>
                        </tr>
                        {c.subchapters && c.subchapters.length > 0 && c.subchapters.map((sc, sidx) => (
                          <tr key={`${idx}-${sidx}-${forceRerender}`}>
                            <td />
                            <td className="truncate px-2.5 pb-2 pl-5 text-[13px] text-muted" title={sc.name}>{sc.name || `Subchapter ${sidx + 1}`}</td>
                            <td className="px-2.5 pb-2">
                              <input
                                type="number"
                                placeholder="Start"
                                value={sc.alignedStart != null ? String(sc.alignedStart) : ''}
                                onChange={(e) => handleSubchapterChange(idx, sidx, 'alignedStart', e.target.value)}
                                className={`ml-auto border-line-strong ${CELL_INPUT}`}
                              />
                            </td>
                            <td className="px-2.5 pb-2">
                              <input
                                type="number"
                                placeholder="End"
                                value={sc.alignedEnd != null ? String(sc.alignedEnd) : ''}
                                onChange={(e) => handleSubchapterChange(idx, sidx, 'alignedEnd', e.target.value)}
                                className={`ml-auto border-line-strong ${CELL_INPUT}`}
                              />
                            </td>
                            <td colSpan={2} />
                          </tr>
                        ))}
                      </tbody>
                      );
                    })}
                  </table>
                  <PagedFooter className="mt-3 border-t border-hairline pt-3" from={rangePg.from} to={rangePg.to} total={rangePg.total} page={rangePg.page} pageCount={rangePg.pageCount} onPage={rangePg.setPage} label="Chapter ranges pagination" />
                </div>
              )}

              <div className="mt-3.5 flex flex-wrap gap-2.5">
                <Button type="button" variant="ghost" size="sm" disabled={!filePages || !chapters || chapters.length === 0} onClick={() => { if (filePages) { setLastChapterEndInput(String(filePages)); setLastChapterEnd(filePages); } }}>
                  {filePages ? `Set last end to page ${filePages}` : 'Set last end to the last page'}
                </Button>
                <Button type="button" variant="ghost" size="sm" icon={<Plus size={14} aria-hidden />} onClick={() => setChapters((prev) => [...(prev || []), { name: `Chapter ${(prev?.length ?? 0) + 1}`, alignedStart: null, alignedEnd: null }])}>Add chapter</Button>
              </div>

              {showStep3Preview && previewUrl && (
                <div className="mt-[18px] overflow-hidden rounded-[14px] border border-line">
                  <iframe title="PDF Preview (Step 3)" src={previewUrl} className="h-[60vh] w-full" />
                </div>
              )}

              {errorBox}

              <RagStepFooter onBack={() => setCurrentStep(2)} note={chapters && chapters.length > 0 ? `${plural(chapters.length, 'chapter')} · ${plural(subchapterTotal, 'subchapter')}` : 'No chapters yet'}>
                <Button
                  type="button"
                  onClick={() => { setCurrentStep(4); }}
                  disabled={!chapters || chapters.length === 0}
                >
                  {nextLabel('Map to curriculum')}
                </Button>
              </RagStepFooter>
            </>
          )}

          {currentStep === 4 && (
            <>
              {(!chapters || chapters.length === 0) ? (
                <div className="rounded-[14px] border border-dashed border-line-strong p-4 text-center text-sm text-muted">
                  No parsed chapters to map. Go back and generate chapters first.
                </div>
              ) : (
                <>
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                    <span className="text-[13px] text-muted">{mappedCount} of {chapters.length} matched{matchScope ? ` to ${matchScope} chapters` : ''}<span className="sr-only"> ({plural(dbChapters.length, 'course chapter')} found for this subject)</span></span>
                    <div className="flex items-center gap-2">
                      <Button type="button" variant="secondary" size="sm" onClick={() => { autoMatchAll(); }}>Auto-match by name</Button>
                      <Button type="button" variant="ghost" size="sm" onClick={() => { setChapterMappings({}); setAutoScores({}); }}>Clear</Button>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[640px] text-sm">
                      <thead>
                        <tr>
                          <th className={TH}>From the PDF</th>
                          <th className={TH}>Course chapter</th>
                          <th className={TH}>Match</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mapPg.pageItems.map((c, i0) => {
                          const idx = mapPg.from - 1 + i0;
                          return (
                          <tr key={idx} className="border-t border-hairline">
                            <td className="px-2.5 py-3 align-top">
                              <div className="font-semibold text-ink">{idx + 1}. {c.name || `Chapter ${idx + 1}`}</div>
                              <div className="mt-0.5 text-xs text-muted">Pages: {c.alignedStart ?? '-'} - {c.alignedEnd ?? '-'}</div>
                            </td>
                            <td className="px-2.5 py-3 align-top">
                              <label htmlFor={`mapSelect-${idx}`} className="sr-only">Map to existing chapter (optional)</label>
                              <select id={`mapSelect-${idx}`} aria-label={`Map generated chapter ${idx + 1} to existing chapter`} className={`${RAG_INPUT} !min-h-[38px]`} value={chapterMappings[idx] ?? ''} onChange={(e) => {
                                const v = e.target.value ? parseInt(e.target.value, 10) : null;
                                setChapterMappings(prev => ({ ...prev, [idx]: v }));
                                // A hand pick replaces any auto-match score.
                                setAutoScores(prev => { const { [idx]: _gone, ...rest } = prev; return rest; });
                              }}>
                                <option value="">-- do not map / create new --</option>
                                {dbChapters.map((d:any) => (<option key={d.id} value={d.id}>{d.name ?? d.title ?? d.id}</option>))}
                              </select>
                              {/* University mode: show course-level subchapters and allow mapping of Gemini-provided subchapters */}
                              {institutionMode === 'university' && (
                                <div className="mt-3 rounded-[12px] bg-surface-2 p-3 text-sm">
                                  <div className="mb-2 text-xs text-muted">Course subchapters available: {courseSubchapters.length}</div>
                                  { (c.subchapters && c.subchapters.length > 0) ? (
                                    <div className="space-y-2">
                                      <div className="mb-1 text-xs text-muted">Gemini-provided subchapters (map each to a course subchapter):</div>
                                      {c.subchapters.map((sc, sidx) => (
                                        <div key={`sub-${idx}-${sidx}`} className="flex items-center gap-2">
                                          <div className="flex-1 text-sm text-ink">{sc.name || `Subchapter ${sidx+1}`}</div>
                                          <select aria-label={`Map subchapter ${sidx+1} of chapter ${idx+1}`} className={`${RAG_INPUT} !w-auto max-w-[60%]`} value={subchapterMappings[`${idx}_${sidx}`] ?? ''} onChange={(e) => {
                                            const val = e.target.value ? parseInt(e.target.value, 10) : null;
                                            setSubchapterMappings(prev => ({ ...prev, [`${idx}_${sidx}`]: val }));
                                          }}>
                                            <option value="">-- do not map / create new --</option>
                                            {courseSubchapters.map((cs) => (<option key={cs.id} value={cs.id}>{cs.name}</option>))}
                                          </select>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="text-xs text-muted">No Gemini subchapters parsed for this chapter.</div>
                                  )}
                                </div>
                              )}
                            </td>
                            <td className="px-2.5 py-3 align-top">
                              {chapterMappings[idx] == null
                                ? <Badge tone="danger">Unmatched</Badge>
                                : autoScores[idx] != null ? <Badge tone="success">Auto · {autoScores[idx]}%</Badge> : <Badge tone="info">Manual</Badge>}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <PagedFooter className="mt-3 border-t border-hairline pt-3" from={mapPg.from} to={mapPg.to} total={mapPg.total} page={mapPg.page} pageCount={mapPg.pageCount} onPage={mapPg.setPage} label="Chapter mapping pagination" />
                  </div>
                </>
              )}

              {errorBox}

              <RagStepFooter onBack={() => setCurrentStep(3)} note={chapters && chapters.length > 0 ? `${mappedCount} of ${chapters.length} chapters mapped` : undefined}>
                <Button
                  type="button"
                  onClick={() => {
                    // The chunking mode is chosen on step 5; nothing runs until Run chunking is pressed.
                    setCurrentStep(5);
                  }}
                  disabled={!chapters || chapters.length === 0}
                >
                  {nextLabel('Chunk the book')}
                </Button>
              </RagStepFooter>
            </>
          )}

          {currentStep === 5 && (
            <>
              <div className="grid grid-cols-1 gap-[22px] lg:grid-cols-12">
                <div className="flex min-w-0 flex-col gap-3 lg:col-span-6">
                  <p className={RAG_SECTION_LABEL}>Chunking mode</p>
                  <RadioCards<'text' | 'image'>
                    label="Chunking mode"
                    value={chunkModeChoice}
                    onChange={setChunkModeChoice}
                    options={[
                      { key: 'text', label: 'Text mode', hint: 'Uses the PDF text layer. Fast and accurate for digital textbooks.' },
                      { key: 'image', label: 'Image mode', hint: 'Sends pages to Gemini to read. Use for scanned books.' },
                    ]}
                  />
                  {chunkModeChoice === 'image' && (
                    <FormField label="Pages per upload (PDF)" htmlFor="pagesPerUploadModal">
                      <input id="pagesPerUploadModal" className={RAG_INPUT} type="number" min={1} value={pagesPerUpload} onChange={(e) => setPagesPerUpload(e.target.value)} />
                    </FormField>
                  )}
                  <p className="text-sm text-muted">{chunkModeChoice === 'text'
                    ? 'Each page with text becomes one chunk. Run chunking again after changing chapter ranges.'
                    : 'Pages are sent to Gemini to read, then split into chunks. Run chunking again after changing chapter ranges.'}</p>
                  <div className="flex flex-wrap gap-2.5">
                    <Button type="button" variant="secondary" size="sm" onClick={chunkModeChoice === 'image' ? handleChunkChaptersImageMode : handleChunkChapters} loading={isChunking} disabled={isChunking || !chapters || chapters.length === 0}>{chunkTotal > 0 ? 'Run chunking again' : 'Run chunking'}</Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => { setChunkSummaries([]); setChunkItems([]); }}>Clear chunks</Button>
                  </div>

                  {chunkProgress && chunkProgress.total > 0 && (
                    <div>
                      <div className="mb-1.5 flex justify-between gap-3 text-xs text-muted">
                        <span className="truncate">{chunkProgress.label}</span>
                        <span className="font-mono">{chunkProgress.completed} / {chunkProgress.total}</span>
                      </div>
                      <Track value={percentOf(chunkProgress)} tone="accent" label={chunkProgress.label || 'Chunking progress'} />
                    </div>
                  )}

                  {/* Uploaded files status panel for image/file-based chunking */}
                  {uploadedFiles && uploadedFiles.length > 0 && (
                    <div className="rounded-[14px] bg-surface-2 p-4">
                      <div className="mb-2.5 flex items-center justify-between gap-3">
                        <h3 className="font-semibold text-ink">Upload status</h3>
                        <div className="flex items-center gap-2">
                          <label htmlFor="uploadConcurrencyInput" className="text-sm text-muted">Concurrency</label>
                          <input
                            id="uploadConcurrencyInput"
                            title="Upload concurrency"
                            aria-label="Upload concurrency"
                            type="number"
                            value={uploadConcurrency}
                            min={1}
                            max={10}
                            onChange={(e) => setUploadConcurrency(Number(e.target.value) || 1)}
                            className={`${RAG_INPUT} !w-20`}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        {uploadedFiles.map((entry) => (
                          <div key={entry.name} className="flex items-center justify-between gap-3 rounded-[12px] border border-line bg-surface p-2.5">
                            <div className="min-w-0">
                              <div className="font-medium text-ink">{entry.name} {entry.start ? `(${entry.start}-${entry.end})` : ''}</div>
                              <div className="text-sm text-muted">State: {entry.state} {entry.error ? `— ${entry.error}` : ''}</div>
                            </div>
                            <div className="flex items-center gap-2">
                              {entry.state === 'FAILED' && (
                                <Button type="button" variant="ghost" size="sm" onClick={() => retryUpload(entry.name)}>Retry</Button>
                              )}
                              {entry.state === 'READY' && entry.uri && (
                                <a href={entry.uri} target="_blank" rel="noreferrer" className="text-sm font-semibold text-ink underline underline-offset-2">Open</a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="min-w-0 lg:col-span-6">
                  <div className="mb-2.5 flex items-baseline justify-between gap-3">
                    <h3 className={RAG_SECTION_LABEL}>Chunk summaries</h3>
                    {chunkSummaries && chunkSummaries.length > 0 && <Badge tone={isChunking ? 'warning' : 'success'}>{plural(chunkTotal, 'chunk')}{isChunking ? '' : ' · done'}</Badge>}
                  </div>
                  {chunkSummaries && chunkSummaries.length > 0 ? (
                    <ul>
                      {chunkSummaries.map((s, i) => (
                        <li key={i} className="flex items-center gap-2.5 border-b border-hairline py-2 text-[13px] last:border-b-0">
                          <span className="w-[18px] font-mono text-muted">{i + 1}</span>
                          <span className="min-w-0 flex-1 truncate text-ink" title={`${s.name} (pages ${s.start}-${s.end})`}>{s.name}</span>
                          <Track value={(s.chunks / maxChapterChunks) * 100} label={`${s.name} chunks`} className="!h-1.5 w-[90px] flex-none" />
                          <span className="w-[34px] text-right font-mono text-ink">{s.chunks}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="rounded-[14px] border border-dashed border-line-strong p-4 text-center text-sm text-muted">No chunks yet. Run chunking to extract pages.</div>
                  )}
                </div>
              </div>

              {errorBox}

              <RagStepFooter onBack={() => setCurrentStep(4)} note={chunkTotal > 0 ? `${plural(chunkTotal, 'chunk')} from ${plural(chunkSummaries.length, 'chapter')}` : 'No chunks yet'}>
                <Button
                  type="button"
                  onClick={() => { setCurrentStep(6); setTimeout(() => { handleEmbedChunks(); }, 0); }}
                  disabled={!chunkItems || chunkItems.length === 0}
                >
                  {nextLabel('Start embedding')}
                </Button>
              </RagStepFooter>
            </>
          )}

          {currentStep === 6 && (
            <>
              <div className="grid grid-cols-1 gap-[22px] lg:grid-cols-12">
                <div className="flex flex-col gap-3 rounded-[18px] bg-navy p-[22px] text-on-navy lg:col-span-5">
                  <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-on-navy-muted">Embedding</span>
                  {embedProgress && embedProgress.total > 0 ? (
                    <>
                      <span className="font-mono text-[56px] font-semibold leading-none tracking-[-0.04em]">{Math.round(percentOf(embedProgress))}<span className="text-2xl">%</span></span>
                      <Track value={percentOf(embedProgress)} tone="accent" label={embedProgress.label || 'Embedding progress'} className="!bg-navy-2" />
                      <span className="text-[13px] text-on-navy">{embedProgress.completed} of {plural(embedProgress.total, 'chunk')}{isEmbedding && embedRemainingMin ? ` · about ${embedRemainingMin} min left` : ''}</span>
                      <span className="text-xs text-on-navy-muted">{embedProgress.label}</span>
                    </>
                  ) : isEmbedding ? (
                    <span className="text-[13px] text-on-navy">Starting…</span>
                  ) : (
                    <span className="text-[13px] text-on-navy">Not started. {plural(chunkTotal, 'chunk')} ready to embed.</span>
                  )}
                </div>

                <div className="min-w-0 lg:col-span-7">
                  {embedRows.length > 0 && (
                    <>
                      <p className={`mb-1.5 ${RAG_SECTION_LABEL}`}>By chapter</p>
                      <ul aria-label="Embedding by chapter">
                        {embedRows.map((r, i) => (
                          <li key={r.name} className="flex items-center gap-2.5 border-b border-hairline py-2 text-[13px] last:border-b-0">
                            <span className="min-w-0 flex-1 truncate text-ink" title={r.name}>{i + 1}. {r.name}</span>
                            <Badge tone={r.state === 'Done' ? 'success' : r.state === 'Embedding' ? 'accent' : 'neutral'}>{r.state}</Badge>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  {!isEmbedding && embedSummaries.length > 0 && (
                    <RagDevTools>

                      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                        <h3 className={RAG_SECTION_LABEL}>Embedding summary</h3>
                        <div className="flex items-center gap-2">
                          <Button type="button" variant="ghost" size="sm" onClick={() => {
                            const newMap: Record<string, boolean> = {};
                            for (const s of embedSummaries) newMap[s.chapterName] = true;
                            setOpenPreviewChapters(newMap);
                          }}>Expand all</Button>
                          <Button type="button" variant="ghost" size="sm" onClick={() => setOpenPreviewChapters({})}>Collapse all</Button>
                        </div>
                      </div>
                      <p className="mb-2 text-[13px] text-muted">Click Preview to inspect example embeddings for a chapter.</p>

                      <ul>
                        {embedSummaries.map((s, i) => {
                          const details = embedDetails?.[s.chapterName] ?? [];
                          const opened = !!openPreviewChapters[s.chapterName];
                          return (
                            <li key={i} className="border-b border-line py-2">
                              <div className="flex items-center justify-between gap-3 text-[13px]">
                                <span className="min-w-0 flex-1 truncate font-medium text-ink">{s.chapterName}</span>
                                <span className="text-muted">{s.count} embeddings</span>
                                <Button type="button" variant="ghost" size="sm" onClick={() => setOpenPreviewChapters(prev => ({ ...prev, [s.chapterName]: !prev[s.chapterName] }))}>{opened ? 'Hide' : 'Preview'}</Button>
                              </div>
                              {opened && (
                                <div className="mt-2 space-y-2">
                                  {details.length === 0 ? (
                                    <div className="text-xs text-muted">No example details for this chapter (embedding may not have exposed raw responses).</div>
                                  ) : (
                                    details.slice(0, 5).map((d, idx) => (
                                      <div key={idx} className="rounded-[12px] bg-surface-2 p-2.5 text-xs">
                                        <div className="font-medium text-ink">Page {d.page}</div>
                                        <div className="mt-1 line-clamp-3 text-[13px] text-ink">{d.text}</div>
                                        <details className="mt-1 text-[11px] text-muted">
                                          <summary className="cursor-pointer">Raw response</summary>
                                          <pre className="mt-1 max-h-40 overflow-auto rounded-[10px] bg-surface p-2 text-[11px]">{JSON.stringify(d.raw, null, 2)}</pre>
                                        </details>
                                      </div>
                                    ))
                                  )}
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </RagDevTools>
                  )}

                  {(!isEmbedding && (!embedSummaries || embedSummaries.length === 0)) && (
                    <div className="rounded-[14px] border border-dashed border-line-strong p-4 text-center text-sm text-muted">
                      Click "Run embedding" to start processing.
                    </div>
                  )}

                  {isEmbedding && (
                    <div className="flex items-center gap-2 text-sm text-muted"><Spinner size="sm" /> Embedding chunks…</div>
                  )}
                </div>
              </div>

              {errorBox}

              <RagStepFooter
                onBack={() => setCurrentStep(5)}
                note={isEmbedding ? `${embedProgress.completed} of ${embedProgress.total} chunks embedded` : (embedSummaries.length > 0 ? `${plural(embeddedTotal, 'embedding')} ready to save` : `${plural(chunkTotal, 'chunk')} to embed`)}
              >
                <Button
                  type="button"
                  onClick={embedSummaries && embedSummaries.length > 0 ? handleSaveBook : handleEmbedChunks}
                  loading={isEmbedding}
                  disabled={isEmbedding || (chunkItems?.length ?? 0) === 0}
                >
                  {isEmbedding ? 'Embedding…' : (embedSummaries && embedSummaries.length > 0 ? 'Finish and view in library' : 'Run embedding')}
                </Button>
              </RagStepFooter>
            </>
          )}
        </section>
      </div>

      <ConfirmDialog
        isOpen={confirmLeave}
        title="Leave this setup?"
        body="Your progress in this wizard will be lost."
        confirmLabel="Leave"
        cancelLabel="Stay"
        onConfirm={() => { setConfirmLeave(false); navigate('/admin/rag'); }}
        onCancel={() => setConfirmLeave(false)}
      />

      {/* API Key modal rendered at top level when requested */}
      {showApiKeyModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-navy/50 p-4">
          <div role="dialog" aria-modal="true" aria-labelledby="ragApiKeyTitle" className="max-h-[90dvh] w-full max-w-md overflow-y-auto rounded-[20px] border border-line bg-surface p-6 shadow-card">
            <h2 id="ragApiKeyTitle" className="mb-2 font-display text-[17px] font-bold text-ink">Add your Gemini API key</h2>
            <p className="mb-4 text-sm text-muted">We need your API key to call Gemini for chapter extraction and embeddings. The key is stored for your account only.</p>
            <FormField label="Gemini API Key" htmlFor="modalGeminiKey">
              <input id="modalGeminiKey" className={RAG_INPUT} type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="sk-..." />
            </FormField>
            <div className="mt-5 flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setShowApiKeyModal(false)}>Close</Button>
              <Button type="button" onClick={async () => { await saveApiKeyToServer(apiKey); }} icon={isSavingKey ? <Spinner size="sm" /> : undefined}>{isSavingKey ? 'Saving…' : 'Save'}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RagSetup;
